import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { countries } from '../utils/countries';
import './Shipping.css';
import './AddressBlock.css';
import CreateReceipt, { openReceiptWindow } from './CreateReceipt';
import { generateCommercialInvoiceHTML } from './CommercialInvoice';
import { numberToWords } from '../utils/numberToWords';


const Shipping = ({ onBack, userModules, userAccounts, isSuperUser, currentUser, userCity, companySlug }) => {
    const [accounts, setAccounts] = useState([]);
    const [accountComments, setAccountComments] = useState({});
    const [selectedAccount, setSelectedAccount] = useState('');
    const [productType, setProductType] = useState('NON-DOCUMENTS');
    const [noCommercialValue, setNoCommercialValue] = useState(false);
    const [view, setView] = useState('list');

    // Helper function to get country name from code
    const getCountryName = (countryCode) => {
        if (!countryCode) return 'N/A';
        const country = countries.find(c => c.code === countryCode);
        return country ? country.name : countryCode;
    };

    // History State
    const [history, setHistory] = useState([]);
    const [totalHistoryCount, setTotalHistoryCount] = useState(0);
    const [historyPage, setHistoryPage] = useState(1);
    const [viewLabel, setViewLabel] = useState(null); // will hold array of base64 strings
    const [selectedShipments, setSelectedShipments] = useState([]);
    const [viewBilling, setViewBilling] = useState(null);
    const [showDetails, setShowDetails] = useState(null);
    const [historySearch, setHistorySearch] = useState('');
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [visibleCount, setVisibleCount] = useState(10);

    // Individual filters
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterTracking, setFilterTracking] = useState('');
    const [filterRecipient, setFilterRecipient] = useState('');
    const [filterCreatedBy, setFilterCreatedBy] = useState('');
    const [filterCostMin, setFilterCostMin] = useState('');
    const [filterCostMax, setFilterCostMax] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const filterTimeoutRef = useRef(null);

    useEffect(() => {
        fetchHistory();
    }, [currentUser, userModules]);

    // Load company settings when companySlug changes
    useEffect(() => {
        if (companySlug) {
            const loadCompanySettings = async () => {
                try {
                    const apiUrl = process.env.NODE_ENV === 'development'
                        ? `http://localhost:5001/api/companies/${companySlug}`
                        : `/api/companies/${companySlug}`;
                    const res = await axios.get(apiUrl);
                    if (res.data.success && res.data.company) {
                        const company = res.data.company;
                        setCompanySettings({
                            receiptEnabled: company.receiptEnabled !== false,
                            receiptFormat: company.receiptFormat || 'standard',
                            receiptPrefix: company.receiptPrefix || 'WB',
                            receiptStartNumber: company.receiptStartNumber || 1000000,
                            glAccountRanges: company.glAccountRanges || []
                        });
                        // If receipts are disabled, disable commercial invoice too
                        if (company.receiptEnabled === false) {
                            setIncludeCommercialInvoice(false);
                        }
                    }
                } catch (err) {
                    console.error('Failed to load company settings:', err);
                }
            };
            loadCompanySettings();
        }
    }, [companySlug]);

    const fetchHistory = async (page = 1, search = '', append = false, filters = {}) => {
        try {
            if (append) setIsLoadingMore(true);
            else setIsHistoryLoading(true);

            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const apiPath = companySlug ? `/api/companies/${companySlug}/billings` : '/api/billing/list';
            const baseUrl = process.env.NODE_ENV === 'development'
                ? `http://localhost:5001${apiPath}`
                : apiPath;

            const params = new URLSearchParams();
            params.append('page', page);

            const limit = 10;
            params.append('limit', limit);

            if (search) params.append('search', search);
            if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
            if (filters.dateTo) params.append('dateTo', filters.dateTo);
            if (filters.tracking) params.append('tracking', filters.tracking);
            if (filters.recipient) params.append('recipient', filters.recipient);
            if (filters.createdBy) params.append('createdBy', filters.createdBy);
            if (filters.costMin) params.append('costMin', filters.costMin);
            if (filters.costMax) params.append('costMax', filters.costMax);
            if (filters.status) params.append('status', filters.status);

            const res = await axios.get(`${baseUrl}?${params.toString()}`, { headers });
            if (res.data.success) {
                const billings = res.data.billings || [];
                const totalCount = res.data.totalCount || res.data.total || 0;

                if (append) {
                    setHistory(prev => [...prev, ...billings]);
                } else {
                    setHistory(billings);
                    setTotalHistoryCount(totalCount);
                    setHistoryPage(1);
                    // Keep selections even when filtering to allow selective bulk operations
                    // setSelectedShipments([]); 
                }
            }
        } catch (err) {
            console.error('Fetch history error:', err);
        } finally {
            setIsLoadingMore(false);
            setIsHistoryLoading(false);
        }
    };
    const handleLoadMore = () => {
        const nextPage = historyPage + 1;
        setHistoryPage(nextPage);
        const filters = {
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            tracking: filterTracking,
            recipient: filterRecipient,
            createdBy: filterCreatedBy,
            costMin: filterCostMin,
            costMax: filterCostMax,
            status: filterStatus
        };
        fetchHistory(nextPage, historySearch, true, filters);
    };

    const handleSearch = (e) => {
        const val = e.target.value;
        setHistorySearch(val);
        // Reset to page 1 for new search
        const filters = {
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            tracking: filterTracking,
            recipient: filterRecipient,
            createdBy: filterCreatedBy,
            costMin: filterCostMin,
            costMax: filterCostMax,
            status: filterStatus
        };
        fetchHistory(1, val, false, filters);
    };

    const handleVoidShipment = async (trackingNumber) => {
        if (!window.confirm(`Are you sure you want to void shipment ${trackingNumber}?`)) return;
        try {
            // Optimistic Update
            setHistory(prev => prev.map(item =>
                item.trackingNumber === trackingNumber ? { ...item, status: 'voided' } : item
            ));

            const apiUrl = process.env.NODE_ENV === 'development' ? `http://localhost:5001/api/shipping/void/${trackingNumber}` : `/api/shipping/void/${trackingNumber}`;
            await axios.delete(apiUrl);

            fetchHistory();
            alert('Shipment Voided Successfully');
        } catch (err) {
            alert('Failed to void: ' + (err.response?.data?.message || err.message));
            fetchHistory(); // Rollback/Sync on error
        }
    };

    // Bulk selection helpers for history table
    const toggleSelectShipment = (trackingNumber) => {
        setSelectedShipments(prev =>
            prev.includes(trackingNumber)
                ? prev.filter(t => t !== trackingNumber)
                : [...prev, trackingNumber]
        );
    };

    const toggleSelectAllShipments = (visibleItems) => {
        if (!visibleItems || visibleItems.length === 0) return;
        const allSelected = visibleItems.every(h => selectedShipments.includes(h.trackingNumber));
        if (allSelected) {
            setSelectedShipments(prev => prev.filter(t => !visibleItems.some(h => h.trackingNumber === t)));
        } else {
            const toAdd = visibleItems.map(h => h.trackingNumber).filter(t => !selectedShipments.includes(t));
            setSelectedShipments(prev => [...prev, ...toAdd]);
        }
    };

    const handleDownloadAllFiltered = async () => {
        try {
            setIsHistoryLoading(true);
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const apiPath = companySlug ? `/api/companies/${companySlug}/billings` : '/api/billing/list';
            const baseUrl = process.env.NODE_ENV === 'development'
                ? `http://localhost:5001${apiPath}`
                : apiPath;

            const params = new URLSearchParams();
            params.append('page', 1);
            params.append('limit', 5000); // Fetch all matching records (up to 5000)

            if (historySearch) params.append('search', historySearch);
            if (filterDateFrom) params.append('dateFrom', filterDateFrom);
            if (filterDateTo) params.append('dateTo', filterDateTo);
            if (filterTracking) params.append('tracking', filterTracking);
            if (filterRecipient) params.append('recipient', filterRecipient);
            if (filterCreatedBy) params.append('createdBy', filterCreatedBy);
            if (filterCostMin) params.append('costMin', filterCostMin);
            if (filterCostMax) params.append('costMax', filterCostMax);
            if (filterStatus) params.append('status', filterStatus);

            const res = await axios.get(`${baseUrl}?${params.toString()}`, { headers });
            if (res.data.success) {
                const allResults = res.data.billings || [];
                if (allResults.length === 0) {
                    alert('No matching records to download.');
                    return;
                }

                // Use the existing formatting logic but for allResults
                downloadDataAsCSV(allResults);
            }
        } catch (err) {
            console.error('Download All error:', err);
            alert('Failed to download filtered results.');
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const downloadDataAsCSV = (data) => {
        const headers = [
            'Date', 'Tracking #', 'Status', 'Account #', 'Product Type',
            'Shipper Name', 'Shipper Address', 'Shipper City', 'Shipper Country',
            'Recipient Name', 'Recipient Company', 'Recipient Address', 'Recipient City', 'Recipient Country', 'Recipient Phone', 'Recipient Email',
            'Ref 1', 'Ref 2', 'Description', 'Weight (KGS)', 'Pkgs',
            'Freight (USD)', 'Fuel Surcharge (USD)', 'SBC (USD)', 'IPF (USD)', 'Insurance (USD)',
            'Delivery/Handling USD', 'Peak/Special USD', 'Tax (USD)', 'Total (USD)',
            'Exchange Rate', 'Fuel %', 'DDP Cost (PKR)', 'Total Cost (PKR)', 'Created By'
        ];

        const rows = data.map(item => {
            const b = item.detailedBreakdown || {};
            const totalSurcharges = parseFloat(b.deliverySurchargeUSD || 0) + parseFloat(b.largePkgSurchargeUSD || 0) + parseFloat(b.additionalHandlingUSD || 0);
            const peakSpecial = parseFloat(b.peakChargesUSD || 0) + parseFloat(b.specialSurchargeUSD || 0);
            const finalPKR = b.grandTotalWithDDPPKR || b.grandTotalPKR || b.totalPKR || '';
            const ddpPKR = b.grandTotalWithDDPPKR ? 'YES' : 'NO';

            return [
                item.shipmentDate ? new Date(item.shipmentDate).toLocaleString() : '',
                item.trackingNumber || '',
                item.status ? item.status.toUpperCase() : 'ACTIVE',
                item.accountNumber || '',
                item.productType || '',
                item.shipper?.name || '',
                item.shipper?.address1 || '',
                item.shipper?.city || '',
                item.shipper?.country || '',
                item.destination?.name || '',
                item.destination?.company || '',
                item.destination?.address1 || '',
                item.destination?.city || '',
                item.destination?.country || '',
                item.destination?.phone || '',
                item.destination?.email || '',
                item.reference1 || '',
                item.reference2 || '',
                item.description || '',
                item.billingWeight || b.totalWeight || '',
                item.packages?.length || '',
                b.freightUSD || '',
                b.fuelSurchargeUSD || '',
                b.sbcUSD || '',
                b.ipfUSD || '',
                b.insuranceUSD || '',
                totalSurcharges.toFixed(2),
                peakSpecial.toFixed(2),
                b.salesTaxUSD || '',
                b.totalUSD || '',
                b.rateUsed || '',
                b.fuelUsed || '',
                ddpPKR,
                finalPKR,
                item.createdBy || item.user || ''
            ];
        });

        const csvContent = [
            headers.map(h => `"${h}"`).join(','),
            ...rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `shipments_filtered_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadShipments = () => {
        const selected = history.filter(h => selectedShipments.includes(h.trackingNumber));
        if (!selected.length) {
            alert('Please select at least one shipment from the current view to download. Or use "Download All Filtered".');
            return;
        }
        downloadDataAsCSV(selected);
    };

    const handleBulkViewLabels = () => {
        const selected = history.filter(h => selectedShipments.includes(h.trackingNumber));
        const labels = [];
        selected.forEach(item => {
            if (item.labels && item.labels.length) labels.push(...item.labels);
            else if (item.labelImage) labels.push(item.labelImage);

            if (item.upsCommercialInvoice) {
                labels.push(item.upsCommercialInvoice);
            }
        });
        if (!labels.length) {
            alert('No label images found for the selected shipment(s). Labels might not have been saved or the shipment was created without labels.');
            return;
        }
        setViewLabel(labels);
    };

    const handleBulkViewBilling = () => {
        if (selectedShipments.length !== 1) return;
        const tracking = selectedShipments[0];
        const item = history.find(h => h.trackingNumber === tracking);
        if (item) {
            console.log('[Shipping] Viewing billing for:', tracking, {
                hasDetailedBreakdown: !!item.detailedBreakdown,
                peakChargesUSD: item.detailedBreakdown?.peakChargesUSD,
                peakChargesPKR: item.detailedBreakdown?.peakChargesPKR,
                fullBreakdown: item.detailedBreakdown
            });
            setViewBilling(item);
        }
    };

    const handleBulkCommercialInvoice = async () => {
        if (selectedShipments.length !== 1) return;
        const tracking = selectedShipments[0];
        const item = history.find(h => h.trackingNumber === tracking);
        if (!item) return;

        if (item.upsCommercialInvoice) {
            try {
                const isPdf = item.upsCommercialInvoice.startsWith('JVBERi0');
                const mimeType = isPdf ? 'application/pdf' : 'image/png';
                const dataUrl = `data:${mimeType};base64,${item.upsCommercialInvoice}`;

                const win = window.open();
                if (win) {
                    if (isPdf) {
                        win.document.write(`<iframe width="100%" height="100%" style="border:none;" src="${dataUrl}"></iframe>`);
                    } else {
                        win.document.write(`<div style="display:flex; justify-content:center; padding:20px;"><img src="${dataUrl}" style="max-width:100%; box-shadow:0 0 10px rgba(0,0,0,0.1);" /></div>`);
                    }
                    win.document.title = `Commercial Invoice - ${item.trackingNumber}`;
                    win.document.body.style.margin = '0';
                    win.document.body.style.backgroundColor = '#525659';
                    win.document.close();
                } else {
                    alert('Pop-up blocked. Please allow pop-ups for this site.');
                }
            } catch (err) {
                console.error('Failed to display UPS commercial invoice', err);
                alert('Failed to display UPS commercial invoice');
            }
            return;
        }

        // Ensure we have an invoice number (server may generate it)
        let invoiceNo = item.invoiceNo;
        if (!invoiceNo) {
            try {
                const apiUrl = process.env.NODE_ENV === 'development'
                    ? `http://localhost:5001/api/shipping/invoice/${tracking}`
                    : `/api/shipping/invoice/${tracking}`;
                const res = await axios.get(apiUrl);
                if (res.data.success) invoiceNo = res.data.invoiceNo;
                // update local state
                setHistory(prev => prev.map(h => h.trackingNumber === tracking ? { ...h, invoiceNo: invoiceNo } : h));
            } catch (err) {
                alert('Failed to generate invoice number: ' + (err.response?.data?.message || err.message));
                return;
            }
        }

        // Build data object expected by generateCommercialInvoiceHTML
        const invoiceShipper = getEffectiveOrigin(item.shipper || {}, item.origin || {});
        const ciData = {
            shipper: invoiceShipper,
            destination: item.destination || {},
            products: item.products || item.detailedProducts || [],
            invoiceNo: invoiceNo || '',
            commodityInvoiceNo: item.commodityInvoiceNo || '',
            exportReason: item.exportReason || '',
            countryOfOrigin: getCountryName(item.products?.find(p => p.countryOfOrigin)?.countryOfOrigin || item.countryOfOrigin || item.origin?.country || item.shipper?.country) || item.countryOfOrigin || '',
            declarationStatement: item.declarationStatement || '',
            currencyType: item.currencyType || item.currency || 'USD',
            freightValue: item.freightValue || '',
            date: new Date(item.shipmentDate).toLocaleDateString('en-GB'),
            trackingNumber: item.trackingNumber,
            iossNumber: item.iossNumber || '',
            insuranceValue: item.shipmentInsuranceChargeUSD,
            shipmentInsuranceDeclaredValue: item.shipmentInsuranceDeclaredValue || 0
        };

        try {
            const htmlFragment = generateCommercialInvoiceHTML(ciData);
            const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Commercial Invoice - ${ciData.trackingNumber}</title></head><body>${htmlFragment}</body></html>`;
            const win = window.open('', '_blank');
            if (win) {
                win.document.write(fullHtml);
                win.document.close();
            }
        } catch (err) {
            console.error('Failed to generate commercial invoice', err);
            alert('Failed to generate commercial invoice');
        }
    };

    const handleBulkDuplicate = () => {
        if (selectedShipments.length !== 1) return;
        const tracking = selectedShipments[0];
        const item = history.find(h => h.trackingNumber === tracking);
        if (item) handleDuplicateShipment(item);
    };

    const handleBulkProcessDraft = () => {
        if (selectedShipments.length !== 1) return;
        const tracking = selectedShipments[0];
        const item = history.find(h => h.trackingNumber === tracking);
        if (!item || item.status !== 'draft') return;

        // Load draft data into the form
        setView('create');
        setSelectedAccount(item.selectedAccount);
        setShipper(item.shipper);
        setOrigin(item.origin);
        setDestination(item.destination || item.consignee); // Support both field names
        setPackages(item.packages || []);
        setProducts(item.products || []);
        setIsDDP(item.isDDP);
        if (item.ddpTransportationPayer) setDdpTransportationPayer(item.ddpTransportationPayer);
        if (item.ddpDutyTaxPayer) setDdpDutyTaxPayer(item.ddpDutyTaxPayer);
        setRef1(item.reference1);
        setRef2(item.reference2);
        setProductType(item.productType);
        setNoCommercialValue(item.noCommercialValue);
        setCommodityInvoiceNo(item.commodityInvoiceNo);
        setIncludeCommercialInvoice(item.includeCommercialInvoice);
        setExportReason(item.exportReason);
        setCountryOfOrigin(item.countryOfOrigin);
        setDeclarationStatement(item.declarationStatement);
        setCurrencyType(item.currencyType);
        setGeneralDescription(item.generalDescription);
        setPaymentMethod(item.paymentMethod);
        setCollectAmount(item.collectAmount);
        setTransactionId(item.transactionId);
        setIsPaymentReceived(item.isPaymentReceived);
        setShipmentInsuranceDeclaredValue(item.shipmentInsuranceDeclaredValue || '');

        // Scroll to form
        window.scrollTo(0, 0);
    };

    const handleDeleteShipment = async (trackingNumber) => {
        if (!window.confirm(`Are you sure you want to permanently delete shipment ${trackingNumber} from history?`)) return;
        try {
            const apiUrl = process.env.NODE_ENV === 'development'
                ? `http://localhost:5001/api/shipping/delete/${trackingNumber}`
                : `/api/shipping/delete/${trackingNumber}`;
            await axios.delete(apiUrl);
            fetchHistory();
            alert('Shipment record deleted successfully');
        } catch (err) {
            alert('Failed to delete: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleBulkDeleteShipments = async () => {
        const toDelete = history.filter(h => selectedShipments.includes(h.trackingNumber) && h.status === 'voided');
        if (!toDelete.length) {
            alert('Only voided shipments can be deleted from history.');
            return;
        }
        if (!window.confirm(`Are you sure you want to permanently delete ${toDelete.length} voided shipment(s) from history?`)) return;

        try {
            setLoading(true);
            for (const item of toDelete) {
                const apiUrl = process.env.NODE_ENV === 'development'
                    ? `http://localhost:5001/api/shipping/delete/${item.trackingNumber}`
                    : `/api/shipping/delete/${item.trackingNumber}`;
                await axios.delete(apiUrl);
            }

            setSelectedShipments([]);
            await fetchHistory();
            alert('Selected voided shipment(s) deleted successfully');
        } catch (err) {
            alert('Failed to delete some shipments: ' + (err.response?.data?.message || err.message));
            fetchHistory();
        } finally {
            setLoading(false);
        }
    };

    const handleBulkVoidShipments = async () => {
        const toVoid = history.filter(h => selectedShipments.includes(h.trackingNumber) && h.status !== 'voided');
        if (!toVoid.length) return;
        if (!window.confirm(`Are you sure you want to void ${toVoid.length} shipment(s)?`)) return;

        try {
            // Optimistic Update
            setHistory(prev => prev.map(item =>
                selectedShipments.includes(item.trackingNumber) ? { ...item, status: 'voided' } : item
            ));

            for (const item of toVoid) {
                const apiUrl = process.env.NODE_ENV === 'development'
                    ? `http://localhost:5001/api/shipping/void/${item.trackingNumber}`
                    : `/api/shipping/void/${item.trackingNumber}`;
                await axios.delete(apiUrl);
            }

            setSelectedShipments([]); // Clear selection
            await fetchHistory(); // Wait for sync
            alert('Selected shipment(s) voided successfully');
        } catch (err) {
            alert('Failed to void some shipments: ' + (err.response?.data?.message || err.message));
            fetchHistory(); // Rollback/Sync on error
        }
    };

    // Shipment State
    const [shipper, setShipper] = useState({
        name: '',
        company: '',
        attention: '',
        phone: '',
        email: '',
        taxId: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'PK',
        type: '01', // 01 = Business, 02 = Consumer
        account: '' // this is the G/L field
    });

    const [origin, setOrigin] = useState({
        name: '',
        company: '',
        attention: '',
        phone: '',
        email: '',
        taxId: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'PK',
        type: '01', // 01 = Business, 02 = Consumer
        account: '' // Optional origin account
    });

    const isPlaceholderAddress = (addr) => {
        if (!addr || typeof addr !== 'object') return false;
        const normalize = (value) => (value || '').toString().trim().toLowerCase();
        return normalize(addr.name) === 'web shipping'
            && normalize(addr.address1) === 'sialkot street'
            && normalize(addr.city) === 'sialkot'
            && normalize(addr.postalCode) === '51310'
            && normalize(addr.country) === 'pk';
    };

    const getEffectiveOrigin = (shipperAddr, originAddr) => {
        if (!originAddr || typeof originAddr !== 'object' || isPlaceholderAddress(originAddr)) {
            return shipperAddr || {};
        }
        // Only merge origin fields that are non-empty so blank origin fields
        // don't overwrite valid shipper values (e.g. address1, city).
        const nonEmptyOrigin = Object.fromEntries(
            Object.entries(originAddr).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
        );
        return { ...shipperAddr, ...nonEmptyOrigin };
    };

    const [ref1, setRef1] = useState('');
    const [ref2, setRef2] = useState('');
    const [userGL, setUserGL] = useState('');
    const [autoGL, setAutoGL] = useState('');

    const [shipperNotify, setShipperNotify] = useState({
        enabled: false, ship: true, exception: true, delivery: true
    });
    const [receiverNotify, setReceiverNotify] = useState({
        enabled: false, ship: true, exception: true, delivery: true
    });

    const [destination, setDestination] = useState({
        name: '',
        company: '',
        attention: '',
        phone: '',
        email: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US',
        taxId: '',
        taxIdType: 'TAX ID',
        type: '01' // 01 = Business, 02 = Consumer
    });

    const [packages, setPackages] = useState([
        { weight: '', length: '', width: '', height: '', description: '', additionalHandling: false }
    ]);

    const [products, setProducts] = useState([
        { description: '', hsCode: '', value: '', weight: '', quantity: '1', uom: 'PCS', countryOfOrigin: 'PK', merchantProductId: '', manufactureProductId: '', standardisedProductId: '' }
    ]);

    // Shared Invoice Number for all commodities (used on commercial invoice)
    const [commodityInvoiceNo, setCommodityInvoiceNo] = useState('');
    const [freightValue, setFreightValue] = useState('');
    const [exportReason, setExportReason] = useState('Sale');
    const [countryOfOrigin, setCountryOfOrigin] = useState('PK');
    const [declarationStatement, setDeclarationStatement] = useState('I declare that all the information contained in this invoice is true and correct.');
    const [currencyType, setCurrencyType] = useState('USD');
    const [includeCommercialInvoice, setIncludeCommercialInvoice] = useState(true);
    const [receiptNumber, setReceiptNumber] = useState('');
    const [generalDescription, setGeneralDescription] = useState('');
    const [iossNumber, setIossNumber] = useState('');
    const [shipmentInsuranceDeclaredValue, setShipmentInsuranceDeclaredValue] = useState('');

    const [isDDP, setIsDDP] = useState(false);
    const [ddpTransportationPayer, setDdpTransportationPayer] = useState('BillShipper');
    const [ddpDutyTaxPayer, setDdpDutyTaxPayer] = useState('BillShipper');
    const [useDDPThirdParty, setUseDDPThirdParty] = useState(false);
    const [ddpThirdPartyAccount, setDdpThirdPartyAccount] = useState('');
    const [showThirdPartyModal, setShowThirdPartyModal] = useState(false);
    const [ddpThirdPartyInfo, setDdpThirdPartyInfo] = useState({
        customerId: '', name: '', attention: '', address1: '', address2: '', address3: '',
        city: '', state: '', postalCode: '', country: 'US', phone: '', fax: '', account: '',
        alsoUseForDutyTax: false
    });
    const [ddpReceiverInfo, setDdpReceiverInfo] = useState({
        account: '', postalCode: '', country: 'US'
    });
    const [taxSelection, setTaxSelection] = useState('auto'); // 'auto'|'15'|'16'|'0'
    const [loading, setLoading] = useState(false);
    const [rates, setRates] = useState(null);
    const [shipmentResult, setShipmentResult] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [debugSteps, setDebugSteps] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const getFieldGroupClass = (fieldName) => fieldErrors[fieldName] ? 'field-group error' : 'field-group';
    const [expandedAccountId, setExpandedAccountId] = useState(null);
    const [accountRates, setAccountRates] = useState({});
    const [loadingAccounts, setLoadingAccounts] = useState({});
    const [accountErrors, setAccountErrors] = useState({});

    // Payment State
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [collectAmount, setCollectAmount] = useState('');
    const [transactionId, setTransactionId] = useState('');
    const [isPaymentReceived, setIsPaymentReceived] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const [savedAddresses, setSavedAddresses] = useState([]);
    const [savedProducts, setSavedProducts] = useState([]);
    const [selectedShipperId, setSelectedShipperId] = useState('');
    const [selectedDestId, setSelectedDestId] = useState('');

    // Company Settings State
    const [companySettings, setCompanySettings] = useState({
        receiptEnabled: false,
        receiptFormat: 'standard',
        receiptPrefix: 'WB',
        receiptStartNumber: 1000000,
        glAccountRanges: []
    });

    useEffect(() => {
        if (!companySlug || !companySettings.glAccountRanges || !companySettings.glAccountRanges.length || !selectedAccount) {
            setAutoGL('');
            // Optional: reset fields if manual values exist but we want strict auto-pop? 
            // The user said "show ONLY when selected", so if no account selected, we should probably clear it if it was auto-populated.
            if (!selectedAccount) {
                setShipper(prev => ({ ...prev, account: '' }));
                setOrigin(prev => ({ ...prev, account: '' }));
                setRef1('');
            }
            return;
        }

        let billableWeight = 0;
        packages.forEach(pkg => {
            const act = parseFloat(pkg.weight) || 0;
            const l = parseFloat(pkg.length) || 0;
            const w = parseFloat(pkg.width) || 0;
            const h = parseFloat(pkg.height) || 0;
            const vol = (l * w * h) / 5000;
            billableWeight += Math.max(act, vol);
        });

        if (billableWeight > 0) {
            const matchedRange = companySettings.glAccountRanges.find(r =>
                billableWeight >= parseFloat(r.minWeight) &&
                billableWeight <= parseFloat(r.maxWeight) &&
                (!r.upsAccount || r.upsAccount.trim().toUpperCase() === selectedAccount.trim().toUpperCase())
            );
            if (matchedRange) {
                setAutoGL(matchedRange.account);
                setShipper(prev => ({ ...prev, account: matchedRange.account }));
                setOrigin(prev => ({ ...prev, account: matchedRange.account }));
                setRef1(matchedRange.account);
            } else {
                setAutoGL('');
                setShipper(prev => ({ ...prev, account: '' }));
                setOrigin(prev => ({ ...prev, account: '' }));
                setRef1('');
            }
        } else {
            setAutoGL('');
            setShipper(prev => ({ ...prev, account: '' }));
            setOrigin(prev => ({ ...prev, account: '' }));
            setRef1('');
        }
    }, [packages, companySettings.glAccountRanges, companySlug, selectedAccount]);

    // Comprehensive sanitization to prevent circular reference errors
    const sanitizeForSerialization = (obj, seen = new WeakSet()) => {
        // Handle null, undefined, primitives
        if (obj === null || obj === undefined) return obj;
        const objType = typeof obj;

        if (objType !== 'object') {
            // Return primitives as-is
            if (objType === 'function') return undefined;
            return obj;
        }

        // Prevent infinite recursion with circular ref detection
        if (seen.has(obj)) return undefined;

        // Handle Date objects
        if (obj instanceof Date) return obj.toISOString();

        // Handle special non-serializable types - DOM elements, React internals, etc
        if (obj.nodeType || // DOM element
            obj.constructor?.name === 'SyntheticEvent' || // React event
            obj.constructor?.name === 'FiberNode' || // React fiber
            obj.__reactFiber$ || // React fiber property
            obj._owner !== undefined || // React component
            obj.style instanceof CSSStyleDeclaration || // DOM style
            obj instanceof HTMLElement || // Any DOM element
            obj instanceof Node) { // Any DOM node
            return undefined;
        }

        seen.add(obj);

        if (Array.isArray(obj)) {
            return obj.map(item => sanitizeForSerialization(item, seen));
        }

        // Handle plain objects
        const cleaned = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const val = obj[key];
                const sanitized = sanitizeForSerialization(val, seen);
                // Only include if the sanitized value is not undefined
                if (sanitized !== undefined) {
                    cleaned[key] = sanitized;
                }
            }
        }

        return cleaned;
    };

    useEffect(() => {
        fetchAccounts();
        fetchAddresses();
        fetchSavedProducts();
    }, [userAccounts, companySlug]);

    const handleSwitchAddresses = () => {
        const tempFrom = { ...shipper };
        const tempTo = { ...destination };

        setShipper({
            ...tempTo,
            country: tempTo.country || 'PK',
            account: tempFrom.account // Keep the G/L number on the from side
        });

        setDestination({
            ...tempFrom,
            country: tempFrom.country || 'US'
        });
    };

    // load current user's GL number and populate fields
    useEffect(() => {
        const loadUser = async () => {
            if (!currentUser) return;
            try {
                const emailParam = typeof currentUser === 'object' ? currentUser.email : currentUser;
                const apiUrl = process.env.NODE_ENV === 'development'
                    ? `http://localhost:5001/api/users?email=${encodeURIComponent(emailParam)}`
                    : `/api/users?email=${encodeURIComponent(emailParam)}`;
                const res = await axios.get(apiUrl);
                if (res.data.success && res.data.users && res.data.users.length > 0) {
                    const profile = res.data.users[0];
                    if (profile) {
                        if (profile.glNumber) {
                            setUserGL(profile.glNumber);
                            setShipper(prev => ({ ...prev, account: profile.glNumber }));
                            setOrigin(prev => ({ ...prev, account: profile.glNumber }));
                            setRef1(profile.glNumber);
                        } else {
                            setUserGL('');
                            setShipper(prev => ({ ...prev, account: '' }));
                            setOrigin(prev => ({ ...prev, account: '' }));
                            setRef1('');
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to fetch user profile', err);
            }
        };
        loadUser();
    }, [currentUser]);

    // Sync G/L Number (origin.account) with Reference 1
    useEffect(() => {
        if (origin.account) {
            setRef1(origin.account);
        }
    }, [origin.account]);

    const fetchAddresses = async () => {
        try {
            const apiUrl = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/address-book'
                : '/api/address-book';
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.get(apiUrl, { headers });
            if (res.data.success) {
                setSavedAddresses(res.data.addresses);
            }
        } catch (err) {
            console.error('Failed to fetch addresses', err);
        }
    };

    const handleSaveAddress = async (type, data) => {
        const defaultName = data.name || data.company || '';
        const name = prompt('Enter a name for this address:', defaultName);
        if (!name) return;

        try {
            const apiUrl = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/address-book'
                : '/api/address-book';
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const payload = { ...data, name, company: data.company || '', type }; // type: 'shipper', 'origin' or 'destination'
            // Ensure country is set
            if (!payload.country) payload.country = (type === 'destination') ? 'US' : 'PK';

            const res = await axios.post(apiUrl, payload, { headers });
            if (res.data.success) {
                alert('Address saved!');
                fetchAddresses();
            }
        } catch (err) {
            alert('Failed to save address: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteAddress = async (addressId) => {
        if (!window.confirm('Are you sure you want to delete this address?')) return;

        try {
            const apiUrl = process.env.NODE_ENV === 'development'
                ? `http://localhost:5001/api/address-book/${addressId}`
                : `/api/address-book/${addressId}`;
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const res = await axios.delete(apiUrl, { headers });
            if (res.data.success) {
                alert('Address deleted!');
                fetchAddresses();
            }
        } catch (err) {
            alert('Failed to delete address: ' + (err.response?.data?.message || err.message));
        }
    };

    const formatAddressOption = (a) => {
        if (!a) return '';
        const name = (a.name || '').trim();
        const company = (a.company || '').trim();
        if (company && name) {
            if (name.toLowerCase() === company.toLowerCase() || name.toLowerCase().includes(company.toLowerCase())) {
                return name;
            }
            return `${name} - ${company}`;
        }
        return company || name || 'Saved Address';
    };

    // --- Saved Products (Commodity Book) ---
    const fetchSavedProducts = async () => {
        try {
            const apiUrl = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/saved-products'
                : '/api/saved-products';
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.get(apiUrl, { headers });
            if (res.data.success) {
                setSavedProducts(res.data.products);
            }
        } catch (err) {
            console.error('Failed to fetch saved products', err);
        }
    };

    const handleSaveProduct = async (productData) => {
        const label = prompt('Enter a name/label for this product:');
        if (!label) return;

        try {
            const apiUrl = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/saved-products'
                : '/api/saved-products';
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const payload = { ...productData, label };
            const res = await axios.post(apiUrl, payload, { headers });
            if (res.data.success) {
                alert('Product saved!');
                fetchSavedProducts();
            }
        } catch (err) {
            alert('Failed to save product: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteSavedProduct = async (productId) => {
        if (!window.confirm('Delete this saved product?')) return;
        try {
            const apiUrl = process.env.NODE_ENV === 'development'
                ? `http://localhost:5001/api/saved-products/${productId}`
                : `/api/saved-products/${productId}`;
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.delete(apiUrl, { headers });
            if (res.data.success) {
                alert('Product deleted!');
                fetchSavedProducts();
            }
        } catch (err) {
            alert('Failed to delete product: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDuplicateShipment = (shipment) => {
        // Build shipper data with all required fields
        const shipperFromRecord = shipment.shipper || shipment.origin || {};
        const shipperData = {
            name: shipperFromRecord.name || '',
            company: shipperFromRecord.company || '',
            attention: shipperFromRecord.attention || '',
            phone: shipperFromRecord.phone || '',
            email: shipperFromRecord.email || '',
            taxId: shipperFromRecord.taxId || '',
            address1: shipperFromRecord.address1 || '',
            address2: shipperFromRecord.address2 || '',
            city: shipperFromRecord.city || '',
            state: shipperFromRecord.state || '',
            postalCode: shipperFromRecord.postalCode || '',
            country: shipperFromRecord.country || '',
            account: shipperFromRecord.account || ''
        };
        setShipper(shipperData);

        // Build origin data with all required fields
        const originFromRecord = shipment.origin || {};
        const originBase = isPlaceholderAddress(originFromRecord) ? shipperFromRecord : originFromRecord;
        const originData = {
            name: originBase.name || '',
            company: originBase.company || '',
            attention: originBase.attention || '',
            phone: originBase.phone || '',
            email: originBase.email || '',
            taxId: originBase.taxId || '',
            address1: originBase.address1 || '',
            address2: originBase.address2 || '',
            city: originBase.city || '',
            state: originBase.state || '',
            postalCode: originBase.postalCode || '',
            country: originBase.country || '',
            account: originBase.account || ''
        };
        setOrigin(originData);

        // Build destination data with all required fields
        const destFromRecord = shipment.destination || {};
        const destinationData = {
            name: destFromRecord.name || '',
            company: destFromRecord.company || '',
            attention: destFromRecord.attention || '',
            phone: destFromRecord.phone || '',
            email: destFromRecord.email || '',
            address1: destFromRecord.address1 || '',
            address2: destFromRecord.address2 || '',
            city: destFromRecord.city || '',
            state: destFromRecord.state || '',
            postalCode: destFromRecord.postalCode || '',
            country: destFromRecord.country || 'US',
            taxId: destFromRecord.taxId || '',
            taxIdType: destFromRecord.taxIdType || 'TAX ID'
        };
        setDestination(destinationData);

        if (shipment.packages) setPackages(shipment.packages);

        if (shipment.products) {
            setProducts(shipment.products);
        } else if (shipment.detailedBreakdown && shipment.detailedBreakdown.ddp) {
            // Fallback: try to reconstruct from DDP breakdown if possible (not ideal but better than empty)
            // Ideally server will save products now.
        }

        setIsDDP(shipment.isDDP || false);
        if (shipment.ddpTransportationPayer) setDdpTransportationPayer(shipment.ddpTransportationPayer);
        if (shipment.ddpDutyTaxPayer) setDdpDutyTaxPayer(shipment.ddpDutyTaxPayer);
        setUseDDPThirdParty(!!shipment.useDDPThirdParty);
        if (shipment.ddpThirdPartyAccount) setDdpThirdPartyAccount(shipment.ddpThirdPartyAccount);
        if (shipment.ddpThirdPartyInfo) setDdpThirdPartyInfo(shipment.ddpThirdPartyInfo);
        setView('create');
    };

    const fetchAccounts = async () => {
        try {
            let apiUrl;
            if (companySlug) {
                // Company user: fetch from company-scoped endpoint
                apiUrl = process.env.NODE_ENV === 'development'
                    ? `http://localhost:5001/api/companies/${companySlug}/tariffs`
                    : `/api/companies/${companySlug}/tariffs`;
            } else {
                // Global super user: fetch from global endpoint
                apiUrl = process.env.NODE_ENV === 'development'
                    ? 'http://localhost:5001/api/settings/tariffs'
                    : '/api/settings/tariffs';
            }
            const res = await axios.get(apiUrl);
            if (res.data.success) {
                // Extract unique accounts
                let uniqueAccounts = [...new Set(res.data.tariffs.map(t => t.account))];

                // Filter based on user's permitted accounts
                // Super users can see all accounts
                if (isSuperUser || (userAccounts && userAccounts.includes('all'))) {
                    // Keep all accounts
                } else if (userAccounts && userAccounts.length > 0) {
                    uniqueAccounts = uniqueAccounts.filter(acc => userAccounts.includes(acc));
                } else {
                    // No accounts assigned to user
                    uniqueAccounts = [];
                }

                // Extract comments
                const comments = {};
                res.data.tariffs.forEach(t => {
                    if (t.comment) {
                        comments[t.account] = t.comment;
                    }
                });
                setAccountComments(comments);

                setAccounts(uniqueAccounts);
            }
        } catch (err) {
            console.error('Failed to fetch accounts', err);
        }
    };

    const handleAddPackage = () => {
        setPackages([...packages, { weight: '', length: '', width: '', height: '', description: '', additionalHandling: false }]);
    };

    const handleRemovePackage = (index) => {
        setPackages(packages.filter((_, i) => i !== index));
    };

    const handleAddProduct = () => {
        setProducts([...products, { description: '', hsCode: '', value: '', weight: '', quantity: '1', uom: 'PCS', countryOfOrigin: 'PK', merchantProductId: '', manufactureProductId: '', standardisedProductId: '' }]);
    };

    const handleRemoveProduct = (index) => {
        setProducts(products.filter((_, i) => i !== index));
    };

    // Calculate volumetric weight from dimensions
    const calculateVolumetricWeight = (pkg) => {
        if (!pkg.length || !pkg.width || !pkg.height) return 0;
        const volWeight = (parseFloat(pkg.length) * parseFloat(pkg.width) * parseFloat(pkg.height)) / 5000;
        return parseFloat(volWeight.toFixed(2));
    };

    // Get billable weight (max of actual vs volumetric)
    const getBillableWeight = (pkg) => {
        const actual = parseFloat(pkg.weight) || 0;
        const volumetric = calculateVolumetricWeight(pkg);
        return parseFloat(Math.max(actual, volumetric).toFixed(2));
    };

    const calculateTotalWeight = () => {
        return packages.reduce((sum, pkg) => sum + (parseFloat(pkg.weight) || 0), 0);
    };

    // Adjust package billable weights when product box modes are selected.
    // - 10KG PRODUCT BOX: allow total actual weight 1..15kg. If total actual <= 10kg, charge as 10kg (scale billables to total 10).
    // - 25KG PRODUCT BOX: allow total actual weight 15..25kg. If total actual between 15..25kg, charge as 25kg (increase billables to total 25).
    const adjustPackagesForProductBox = (pkgs, productType) => {
        const calcBillable = (p) => {
            const actual = parseFloat(p.weight) || 0;
            const l = parseFloat(p.length) || 0;
            const w = parseFloat(p.width) || 0;
            const h = parseFloat(p.height) || 0;
            const vol = (l * w * h) / 5000;
            return Math.max(actual, vol);
        };

        const actualTotal = pkgs.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
        const billables = pkgs.map(calcBillable);
        const billableTotal = billables.reduce((s, v) => s + v, 0) || 0.0001;

        // Helper to return mapped package objects with proper fields for rating
        const mapPkg = (p, bw) => ({
            weight: p.weight,
            length: p.length,
            width: p.width,
            height: p.height,
            weightUnit: 'KGS',
            dimUnit: 'CM',
            billableWeight: parseFloat(bw).toFixed(2),
            additionalHandling: !!p.additionalHandling
        });

        if (productType === '10KG PRODUCT BOX') {
            // allow 1..15kg
            // If total actual <= 10kg, scale billables so total billable becomes 10 (so UPS will be charged at 10kg)
            if (actualTotal > 0 && actualTotal <= 10) {
                const scale = 10 / billableTotal;
                return pkgs.map((p, i) => mapPkg(p, billables[i] * scale));
            }
            // otherwise leave billables as-is (10kg box supports up to 15kg as allowed range)
            return pkgs.map((p, i) => mapPkg(p, billables[i]));
        }

        if (productType === '25KG PRODUCT BOX') {
            // allow 15..25kg
            // If actual total within 15..25kg, bump total billable to 25kg by adding difference to the first package
            if (actualTotal >= 15 && actualTotal <= 25) {
                const diff = 25 - billableTotal;
                const newBillables = billables.slice();
                newBillables[0] = newBillables[0] + diff;
                return pkgs.map((p, i) => mapPkg(p, newBillables[i]));
            }
            return pkgs.map((p, i) => mapPkg(p, billables[i]));
        }

        // Default: return normal billables
        return pkgs.map((p, i) => mapPkg(p, billables[i]));
    };

    const calculateTotalValue = () => {
        return products.reduce((sum, prod) => sum + ((parseFloat(prod.value) || 0) * (parseFloat(prod.quantity) || 1)), 0);
    };

    const handleGetRates = async (accountToRate = null) => {
        setError('');
        setRates(null);
        setAccountErrors({});
        setFieldErrors({});

        // Determine effective origin: prefer explicit origin fields, fallback to shipper
        const effectiveOrigin = getEffectiveOrigin(shipper, origin);

        // Validation: Check required fields using effective origin
        const validationErrors = {};
        if (!effectiveOrigin.country) validationErrors.originCountry = true;
        if (!effectiveOrigin.city) validationErrors.originCity = true;
        if (!effectiveOrigin.address1) validationErrors.originAddress1 = true;

        if (!destination.country) validationErrors.destinationCountry = true;
        if (!destination.city) validationErrors.destinationCity = true;
        if (!destination.address1) validationErrors.destinationAddress1 = true;
        if (!destination.name) validationErrors.destinationName = true;

        if (!effectiveOrigin.country || !effectiveOrigin.city || !effectiveOrigin.address1) {
            setFieldErrors(validationErrors);
            setError('Please complete origin address details (country, city, address)');
            return;
        }

        if (!destination.country || !destination.city || !destination.address1) {
            setFieldErrors(validationErrors);
            setError('Please complete destination address details (country, city, address)');
            return;
        }

        if (!destination.name) {
            setFieldErrors(validationErrors);
            setError('Please enter recipient name');
            return;
        }

        // Weight Validation for Product Boxes (new rules):
        // - 10KG PRODUCT BOX: allow total actual weight between 1kg and 15kg (inclusive). If actual <=10kg, charge will be forced to 10kg.
        // - 25KG PRODUCT BOX: allow total actual weight between 15kg and 25kg (inclusive). If actual within 15..25kg, charge will be forced to 25kg.
        const totalWeight = calculateTotalWeight();
        if (productType === '10KG PRODUCT BOX') {
            if (totalWeight < 1 || totalWeight > 15) {
                setError('❌ For 10KG Product Box, total weight must be between 1kg and 15kg');
                return;
            }
        } else if (productType === '25KG PRODUCT BOX') {
            if (totalWeight < 15 || totalWeight > 25) {
                setError('❌ For 25KG Product Box, total weight must be between 15kg and 25kg');
                return;
            }
        }

        // Validation for Duty/Tax to Receiver
        if (ddpDutyTaxPayer === 'BillReceiver') {
            if (!ddpReceiverInfo.account) validationErrors.ddpReceiverAccount = true;
            if (!ddpReceiverInfo.postalCode) validationErrors.ddpReceiverPostalCode = true;
            if (!ddpReceiverInfo.country) validationErrors.ddpReceiverCountry = true;

            if (!ddpReceiverInfo.account || !ddpReceiverInfo.postalCode || !ddpReceiverInfo.country) {
                setFieldErrors(validationErrors);
                setError('Receiver Account Number, Postal Code, and Country are required for Duty/Tax to Receiver');
                return;
            }
        }

        setFieldErrors({});

        // Defensive: If accountToRate is an event object, ignore it
        const isEventObject = accountToRate && typeof accountToRate === 'object' && (accountToRate.nativeEvent || accountToRate.preventDefault || accountToRate.type);

        // Target a specific account if provided, or if selectedAccount is set
        const targetAccount = (!isEventObject && accountToRate) ? accountToRate : (selectedAccount || null);

        // If no target account specified and no selectedAccount set, fetch rates for ALL accounts
        if (!targetAccount) {
            if (accounts.length === 0) {
                setError('No accounts available. Please configure tariffs in Settings.');
                return;
            }

            // Fetch rates for all accounts in parallel
            console.log('[Shipping] Fetching rates for all accounts...');
            setLoadingAccounts(accounts.reduce((acc, acc_id) => ({ ...acc, [acc_id]: true }), {}));

            const ratingPromises = accounts.map(async (account) => {
                try {
                    const packagesForRating = adjustPackagesForProductBox(packages, productType);

                    const ratingPayload = {
                        account: account,
                        product: productType,
                        shipper,
                        origin: effectiveOrigin,
                        destination: {
                            ...destination,
                            account: (ddpTransportationPayer === 'BillThirdParty')
                                ? ddpThirdPartyInfo.account
                                : (ddpDutyTaxPayer === 'BillReceiver') ? ddpReceiverInfo.account : destination.account
                        },
                        isDDP,
                        useBillThirdParty: ddpTransportationPayer === 'BillThirdParty',
                        tprAccount: ddpThirdPartyInfo.account,
                        tprAddress: ddpThirdPartyInfo,
                        ddpDutyTaxPayer,
                        ddpReceiverInfo,
                        taxSelection,
                        taxOverride: (taxSelection === 'auto' || taxSelection === 'fixed') ? taxSelection : parseFloat(taxSelection),
                        shipmentInsuranceDeclaredValue: parseFloat(shipmentInsuranceDeclaredValue) || 0,
                        products,
                        packages: packagesForRating.map(p => ({
                            weight: p.weight,
                            length: p.length,
                            width: p.width,
                            height: p.height,
                            weightUnit: p.weightUnit || 'KGS',
                            dimUnit: p.dimUnit || 'CM',
                            packageType: productType === '10KG PRODUCT BOX' ? '25' : (productType === '25KG PRODUCT BOX' ? '24' : '02'),
                            billableWeight: parseFloat(p.billableWeight),
                            additionalHandling: !!p.additionalHandling
                        }))
                    };

                    const ratingUrl = process.env.NODE_ENV === 'development'
                        ? 'http://localhost:5001/api/rating'
                        : '/api/rating';

                    const cleanRatingPayload = sanitizeForSerialization(ratingPayload);
                    const ratingRes = await axios.post(ratingUrl, cleanRatingPayload);

                    if (ratingRes.data.success && ratingRes.data.breakdown) {
                        const rateData = {
                            shippingCharge: parseFloat(ratingRes.data.breakdown.totalUSD),
                            currency: 'USD',
                            billingWeight: ratingRes.data.breakdown.totalWeight,
                            breakdown: ratingRes.data.breakdown,
                            total: ratingRes.data.breakdown.grandTotalWithDDP ? parseFloat(ratingRes.data.breakdown.grandTotalWithDDP) : parseFloat(ratingRes.data.breakdown.totalUSD)
                        };

                        setAccountRates(prev => ({ ...prev, [account]: rateData }));
                        setAccountErrors(prev => ({ ...prev, [account]: null }));
                        return { account, success: true, data: rateData };
                    } else {
                        const errorMsg = ratingRes.data.message || 'No rates available';
                        setAccountErrors(prev => ({ ...prev, [account]: errorMsg }));
                        return { account, success: false, error: errorMsg };
                    }
                } catch (err) {
                    const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch rates';
                    console.error(`[Shipping] Rate Error for account ${account}:`, errorMsg);
                    setAccountErrors(prev => ({ ...prev, [account]: errorMsg }));
                    return { account, success: false, error: errorMsg };
                }
            });

            try {
                const results = await Promise.all(ratingPromises);
                const successCount = results.filter(r => r.success).length;
                const failCount = results.filter(r => !r.success).length;

                if (successCount === 0) {
                    setError(`❌ No rates available for any account. ${failCount} account(s) failed.`);
                } else if (failCount > 0) {
                    setError(`⚠️ Rates fetched for ${successCount} account(s). ${failCount} account(s) failed.`);
                }

                if (!selectedAccount) {
                    const firstSuccess = results.find(r => r.success);
                    if (firstSuccess) {
                        setSelectedAccount(firstSuccess.account);
                    }
                }
            } finally {
                setLoadingAccounts({});
            }
            return;
        }

        // Single account mode
        const accountForRate = targetAccount;
        if (!accountForRate) {
            setError('Please select an account');
            return;
        }

        console.log(`[Shipping] Fetching rates for account: ${accountForRate}`);
        setLoadingAccounts(prev => ({ ...prev, [accountForRate]: true }));

        try {
            const packagesForRating = adjustPackagesForProductBox(packages, productType);

            const ratingPayload = {
                account: accountForRate,
                product: productType,
                shipper,
                origin: effectiveOrigin,
                destination,
                isDDP,
                taxOverride: taxSelection === 'auto' ? undefined : parseFloat(taxSelection),
                shipmentInsuranceDeclaredValue: parseFloat(shipmentInsuranceDeclaredValue) || 0,
                products,
                packages: packagesForRating.map(p => ({
                    weight: p.weight,
                    length: p.length,
                    width: p.width,
                    height: p.height,
                    weightUnit: p.weightUnit || 'KGS',
                    dimUnit: p.dimUnit || 'CM',
                    packageType: productType === '10KG PRODUCT BOX' ? '25' : (productType === '25KG PRODUCT BOX' ? '24' : '02'),
                    billableWeight: parseFloat(p.billableWeight),
                    additionalHandling: !!p.additionalHandling
                }))
            };

            const ratingUrl = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/rating'
                : '/api/rating';

            const cleanRatingPayload = sanitizeForSerialization(ratingPayload);
            const ratingRes = await axios.post(ratingUrl, cleanRatingPayload);

            if (ratingRes.data.success) {
                const breakdown = ratingRes.data.breakdown;
                if (breakdown) {
                    const rateData = {
                        shippingCharge: parseFloat(breakdown.totalUSD),
                        currency: 'USD',
                        billingWeight: breakdown.totalWeight,
                        breakdown: breakdown,
                        total: breakdown.grandTotalWithDDP ? parseFloat(breakdown.grandTotalWithDDP) : parseFloat(breakdown.totalUSD)
                    };

                    // Store rates for this account
                    setAccountRates(prev => ({ ...prev, [accountForRate]: rateData }));
                    setExpandedAccountId(accountForRate);
                    setSelectedAccount(accountForRate);
                    setDebugSteps(null);
                } else {
                    throw new Error('No rating breakdown received.');
                }
            } else {
                setError(ratingRes.data.message || 'Failed to fetch rates');
                setDebugSteps(ratingRes.data.debugSteps || null);
            }

        } catch (err) {
            let errorMsg = 'Failed to calculate rates';
            let steps = null;

            if (err.response?.status === 404) {
                errorMsg = err.response?.data?.message || '⚠️ Rates not available for selected product/weight combination';
                steps = err.response?.data?.debugSteps || null;
            } else if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
                steps = err.response?.data?.debugSteps || null;
            } else if (err.message) {
                errorMsg = err.message;
            }
            console.error(`[Shipping] Rate Error for account ${accountForRate}:`, errorMsg);
            setAccountErrors(prev => ({ ...prev, [accountForRate]: errorMsg }));
            setError(errorMsg);
            setDebugSteps(steps);
        } finally {
            setLoadingAccounts(prev => ({ ...prev, [accountForRate]: false }));
        }
    };

    const handleSaveDraft = async () => {
        setError('');
        setLoading(true);

        try {
            console.log('[Shipping] Saving Draft...');
            console.log('[Shipping] Current User:', currentUser);

            const effectiveOrigin = getEffectiveOrigin(shipper, origin);

            // Extract user email for createdBy field (for history query filtering)
            // IMPORTANT: Use same logic as fetchHistory to ensure query matches saved data
            const userEmail = typeof currentUser === 'string' ? currentUser : (currentUser?.email || currentUser?.username || currentUser?.id || 'Unknown');
            const userId = typeof currentUser === 'string' ? currentUser : (currentUser?.email || currentUser?.username || currentUser?.id || 'Unknown');
            console.log('[Shipping] User Email extracted:', userEmail);
            console.log('[Shipping] User ID extracted:', userId);

            const draftPayload = {
                status: 'draft',
                user: userId, // Always save as string (matching fetchHistory logic)
                createdBy: userEmail, // Use for history filter queries
                shipmentDate: new Date(), // Add shipmentDate so draft appears in history
                shipper,
                origin: effectiveOrigin,
                destination, // Use 'destination' field name for consistency with history display
                packages,
                products,
                isDDP,
                ddpTransportationPayer,
                ddpDutyTaxPayer,
                reference1: ref1,
                reference2: ref2,
                productType,
                totalValue: calculateTotalValue(),
                userCity,
                isDocumentNoCommercial: noCommercialValue,
                description: generalDescription || 'Draft Shipment',
                commodityInvoiceNo,
                freightValue,
                includeCommercialInvoice,
                exportReason,
                countryOfOrigin: getCountryName(products.find(p => p.countryOfOrigin)?.countryOfOrigin || countryOfOrigin || shipper.country || (origin ? origin.country : '')) || countryOfOrigin || '',
                countryOfOriginCode: products.find(p => p.countryOfOrigin)?.countryOfOrigin || countryOfOrigin || shipper.country || (origin ? origin.country : ''),
                declarationStatement,
                currencyType,
                iossNumber,
                taxOverride: taxSelection === 'auto' ? undefined : parseFloat(taxSelection),
                selectedAccount,
                noCommercialValue,
                paymentMethod,
                collectAmount: isPaymentReceived ? parseFloat(String(collectAmount).replace(/[^0-9.]/g, '')) || 0 : 0,
                transactionId,
                isPaymentReceived,
                generalDescription,
                shipmentInsuranceDeclaredValue: parseFloat(shipmentInsuranceDeclaredValue) || 0
            };

            const cleanPayload = sanitizeForSerialization(draftPayload);

            const url = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/shipping/draft/save'
                : '/api/shipping/draft/save';

            const res = await axios.post(url, cleanPayload);

            console.log('[Shipping] Draft Save Response:', res.data);

            if (res.data.success) {
                setError(''); // Clear any previous errors
                setSuccess('✅ Draft saved successfully! You can continue editing or process it later.');
                setTimeout(() => setSuccess(''), 3000);
                console.log('[Shipping] Fetching updated history...');
                fetchHistory();
            }

        } catch (err) {
            console.error('[Shipping] Draft Save Error:', err);
            console.error('[Shipping] Error Response:', err.response?.data);
            setSuccess(''); // Clear success message on error
            setError(err.response?.data?.message || 'Failed to save draft');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDraft = async (trackingNumber) => {
        if (!window.confirm('Are you sure you want to delete this draft?')) return;

        try {
            setIsHistoryLoading(true);
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const url = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/shipping/draft/delete'
                : '/api/shipping/draft/delete';

            const res = await axios.post(url, { trackingNumber }, { headers });
            if (res.data.success) {
                setSuccess('Draft deleted successfully');
                setTimeout(() => setSuccess(''), 3000);
                fetchHistory();
            }
        } catch (err) {
            console.error('Delete draft error:', err);
            setError(err.response?.data?.message || 'Failed to delete draft');
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const handleCreateShipment = async () => {
        // mirror effective origin logic from rating
        const effectiveOrigin = getEffectiveOrigin(shipper, origin);
        const currentRates = accountRates[selectedAccount] || rates;
        if (!currentRates) return;
        setError('');
        setLoading(true);

        try {
            console.log('[Shipping] Creating Shipment...');

            // Validation for Duty/Tax to Receiver
            if (ddpDutyTaxPayer === 'BillReceiver') {
                if (!ddpReceiverInfo.account || !ddpReceiverInfo.postalCode || !ddpReceiverInfo.country) {
                    setError('Receiver Account, Postal Code, and Country are required for Duty/Tax to Receiver');
                    setLoading(false);
                    return;
                }
            }

            // Calculate billable weight for all packages and apply product-box adjustments so UPS receives the intended box weight
            const packagesForCreate = adjustPackagesForProductBox(packages, productType);
            const packagesWithBillableWeight = packages.map((pkg, i) => ({
                ...pkg,
                billableWeight: parseFloat(packagesForCreate[i]?.billableWeight || getBillableWeight(pkg)),
                // Provide actual physical weight separately so backend can use it for labels
                actualWeight: parseFloat(pkg.weight) || 0,
                volumetricWeight: calculateVolumetricWeight(pkg),
                packageType: productType === '10KG PRODUCT BOX' ? '25' : (productType === '25KG PRODUCT BOX' ? '24' : '02'),
                additionalHandling: !!pkg.additionalHandling
            }));

            const payload = {
                accountNumber: selectedAccount,
                user: currentUser || 'Unknown',
                shipper,
                origin: effectiveOrigin,
                consignee: destination,
                packages: packagesWithBillableWeight,
                products,
                isDDP,
                reference1: ref1,
                reference2: ref2,
                productType,
                totalValue: calculateTotalValue(),
                totalDeclaredValue: calculateTotalValue(),
                shipmentInsuranceDeclaredValue: parseFloat(shipmentInsuranceDeclaredValue) || 0,
                userCity,
                isDocumentNoCommercial: noCommercialValue,
                description: generalDescription || (!includeCommercialInvoice && products.length > 0
                    ? (products[0].description || 'Merchandise')
                    : (noCommercialValue
                        ? 'DOCUMENTS - NO COMMERCIAL VALUE'
                        : 'UPS Saver Shipment')),
                commodityInvoiceNo,
                freightValue,
                includeCommercialInvoice,
                exportReason,
                countryOfOrigin: getCountryName(products.find(p => p.countryOfOrigin)?.countryOfOrigin || countryOfOrigin || shipper.country || (origin ? origin.country : '')) || countryOfOrigin || '',
                countryOfOriginCode: products.find(p => p.countryOfOrigin)?.countryOfOrigin || countryOfOrigin || shipper.country || (origin ? origin.country : ''),
                declarationStatement,
                currencyType,
                iossNumber,
                ddpTransportationPayer,
                ddpDutyTaxPayer,
                useBillThirdParty: ddpTransportationPayer === 'BillThirdParty',
                tprAccount: ddpThirdPartyInfo.account,
                tprAddress: ddpThirdPartyInfo,
                ddpReceiverInfo,
                consignee: {
                    ...destination,
                    account: (ddpTransportationPayer === 'BillThirdParty')
                        ? ddpThirdPartyInfo.account
                        : (ddpDutyTaxPayer === 'BillReceiver') ? ddpReceiverInfo.account : destination.account,
                },
                taxOverride: (taxSelection === 'auto' || taxSelection === 'fixed') ? taxSelection : parseFloat(taxSelection),
                detailedBreakdown: currentRates.breakdown,
                shipperNotify,
                receiverNotify,
                payment: isPaymentReceived ? {
                    method: paymentMethod,
                    amount: parseFloat(String(collectAmount).replace(/[^0-9.]/g, '')) || 0,
                    transactionId: transactionId,
                    receivedAt: new Date()
                } : null
            };

            // Sanitize payload to remove any DOM elements or circular references
            const cleanPayload = sanitizeForSerialization(payload);

            console.log('[Shipping] Sending payload to backend:', {
                tracking: cleanPayload.trackingNumber,
                isDDP: cleanPayload.isDDP,
                hasPayment: !!cleanPayload.payment,
                paymentAmount: cleanPayload.payment?.amount,
                paymentMethod: cleanPayload.payment?.method,
                peakChargesUSD: cleanPayload.detailedBreakdown?.peakChargesUSD,
                peakChargesPKR: cleanPayload.detailedBreakdown?.peakChargesPKR,
                breakdownKeys: Object.keys(cleanPayload.detailedBreakdown || {})
            });

            const url = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/shipping/create'
                : '/api/shipping/create';

            const res = await axios.post(url, cleanPayload);

            console.log('[Shipping] Response from backend:', {
                trackingNumber: res.data.trackingNumber,
                isDDP: res.data.isDDP,
                success: res.data.success
            });

            if (res.data.success) {
                setShipmentResult(res.data);
                fetchHistory();
            }

        } catch (err) {
            console.error('[Shipping] Creation Error:', {
                message: err.message,
                status: err.response?.status,
                responseMessage: err.response?.data?.message,
            });
            setError(err.response?.data?.message || 'Failed to create shipment');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShipmentResult(null);
        setRates(null);
        setError('');
        // Keep account and origin, clear destination/packages if needed, or keep for next similar shipment?
        // Usually clearing destination is safer.
        setDestination({ ...destination, name: '', attention: '', phone: '', email: '', address1: '', address2: '', address3: '', city: '', state: '', postalCode: '', taxId: '', taxIdType: 'TAX ID' });
        setPaymentMethod('Cash');
        setCollectAmount('');
        setTransactionId('');
        setIsPaymentReceived(false);
    };

    if (shipmentResult) {
        const labels = shipmentResult.labels && shipmentResult.labels.length > 0
            ? shipmentResult.labels
            : (shipmentResult.labelImage ? [shipmentResult.labelImage] : []);

        const printLabels = () => {
            if (!labels.length) return;
            const win = window.open('');
            const html = `<!DOCTYPE html>
<html>
<head>
  <title>Shipping Labels</title>
  <style>
    @page { size: A4; margin: 10mm; }
    body { margin: 0; padding: 0; }
    .label-page { page-break-after: always; display:flex; justify-content:center; padding:10mm }
    .label-page img { max-width:100%; height:auto }
  </style>
</head>
<body>
  ${labels.map(l => `<div class="label-page"><img src="data:image/gif;base64,${l}"/></div>`).join('')}
</body>
</html>`;
            win.document.write(html);
            win.document.close();
            win.print();
        };

        const downloadLabels = () => {
            if (!labels.length) return;
            labels.forEach((l, idx) => {
                const a = document.createElement('a');
                a.href = `data:image/gif;base64,${l}`;
                const indexSuffix = labels.length > 1 ? `-${idx + 1}` : '';
                a.download = `${shipmentResult.trackingNumber}${indexSuffix}.gif`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            });
        };

        const viewUPSCommercialInvoice = () => {
            const ci = shipmentResult.commercialInvoice;
            if (!ci) {
                alert('No UPS Commercial Invoice was returned for this shipment. This usually means the shipment was created as a domestic or document-only shipment, or the UPS account does not have Commercial Invoice generation enabled.');
                return;
            }
            const isPdf = ci.startsWith('JVBERi') || ci.startsWith('JVB');
            const mimeType = isPdf ? 'application/pdf' : 'image/gif';
            const dataUrl = `data:${mimeType};base64,${ci}`;
            const win = window.open('');
            if (isPdf) {
                win.document.write(`<!DOCTYPE html><html><head><title>Commercial Invoice - ${shipmentResult.trackingNumber}</title><style>body,html{margin:0;padding:0;height:100%;} iframe{width:100%;height:100%;border:none;}</style></head><body><iframe src="${dataUrl}"></iframe></body></html>`);
            } else {
                win.document.write(`<!DOCTYPE html><html><head><title>Commercial Invoice - ${shipmentResult.trackingNumber}</title><style>body{margin:0;background:#333;display:flex;justify-content:center;align-items:flex-start;padding:20px;} img{max-width:100%;background:white;}</style></head><body><img src="${dataUrl}"/></body></html>`);
            }
            win.document.close();
        };

        const downloadAllAsPDF = () => {
            if (!labels.length) return;

            const today = new Date().toLocaleDateString('en-GB');
            // Use the amount actually received from user, not calculated shipping charges
            const displayAmount = isPaymentReceived ? parseFloat(String(collectAmount).replace(/[^0-9.]/g, '')) || 0 : 0;
            const amountLabel = 'PKR';

            // For display in receipt
            const formattedTotalAmount = displayAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

            // For receipt amount in words (use same displayAmount value)
            const amountInWords = numberToWords(Math.floor(displayAmount));

            const receiverName = shipper.name || 'N/A';

            // Get destination country for receipt
            const receiptDestinationCountry = destination.country ? getCountryName(destination.country) : 'N/A';

            const generatedBy = (currentUser && typeof currentUser === 'object')
                ? currentUser.email
                : (currentUser || 'system');

            // Calculate total billable weight (highest of actual vs dimensional for each package)
            const totalBillableWeight = packages.reduce((sum, pkg) => {
                const billableWt = getBillableWeight(pkg);
                return sum + billableWt;
            }, 0).toFixed(2);

            const win = window.open('', '_blank');
            const html = `<!DOCTYPE html>
<html>
<head>
  <title>Shipment Documentation - ${shipmentResult.trackingNumber}</title>
  <style>
    @page { size: A4; margin: 0; }
    body { margin: 0; padding: 0; font-family: Arial, sans-serif; background: #f0f0f0; }
    .page { page-break-after: always; width: 210mm; min-height: 297mm; padding: 10mm; margin: 10mm auto; background: white; box-shadow: 0 0 10px rgba(0,0,0,0.1); box-sizing: border-box; }
    
    /* Label Styles */
    .label-page { display: flex; justify-content: center; align-items: center; }
    .label-page img { max-width: 100%; height: auto; border: 1px solid #eee; }
    
    /* Receipt Styles (Cloned from CreateReceipt.js) */
    .receipt-container { width: 100%; border: 1px solid #eee; padding: 20px; height: 100%; box-sizing: border-box; }
    .receipt-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 25px; }
    .company-name { font-family: "Arial Black", sans-serif; font-weight: 900; font-size: 20px; margin: 0; color: #000; }
    .company-subtitle { font-size: 14px; margin: 5px 0 0 0; color: #333; }
    .header-right { text-align: right; font-size: 13px; line-height: 1.6; }
    .receipt-no-value { font-family: monospace; font-weight: bold; font-size: 26px; border: 2px solid #000; padding: 5px 15px; margin-left: 10px; }
    .receipt-title { font-size: 24px; font-weight: bold; text-decoration: underline; text-align: center; margin: 30px 0; }
    .form-row { display: flex; align-items: flex-end; margin-bottom: 30px; font-size: 16px; }
    .line-container { flex-grow: 1; border-bottom: 1.5px solid #000; margin-left: 10px; padding-bottom: 2px; font-weight: bold; }
    .receipt-footer { margin-top: 60px; display: flex; justify-content: space-between; align-items: flex-end; }
    @media print {
      body { background: white; }
      .page { margin: 0; box-shadow: none; width: 100%; height: 100vh; }
    }
  </style>
</head>
<body>
  ${labels.map((l, idx) => `
    <div class="page label-page">
      <div style="text-align:center; width:100%">
        <h3 style="margin-bottom:20px; color:#666">Shipping Label ${idx + 1} of ${labels.length}</h3>
        <img src="data:image/gif;base64,${l}"/>
        <p style="margin-top:20px; font-family:monospace; font-weight:bold">${shipmentResult.trackingNumber}</p>
      </div>
    </div>
  `).join('')}
  ${(!companySlug) ? `
  <div class="page">
    <div class="receipt-container">
      <div class="receipt-header">
        <div>
          <h1 class="company-name">Universal Logistics Services (Pvt.) Ltd.</h1>
          <p class="company-subtitle">Authorised Service Contractor for UPS.</p>
        </div>
        <div class="header-right">
          <p>D-79, Kehkashan, Clifton Block 5, Karachi.</p>
          <p>Tel: +92 21 35831127-29 | UAN: 111 560 560</p>
          <p>NTN #: 1343872-7</p>
        </div>
      </div>
      
      <div class="receipt-title-section" style="display:flex; justify-content:space-between; align-items:center; margin: 25px 0; border-top: 1px solid #eee; border-bottom: 1px solid #eee; padding: 10px 0;">
        <div style="font-size:14px; display:flex; align-items:center;">Receipt No. <span class="receipt-no-value" style="font-family:'Courier New', monospace; font-weight:bold; font-size:24px; border:2px solid #000; padding:2px 8px; margin-left:10px;">${shipmentResult.invoiceNo || '_______'}</span></div>
        <div class="receipt-title" style="font-size:22px; font-weight:bold; text-decoration:underline; margin:0">RECEIPT</div>
        <div style="font-size:14px;">Date: <b>${today}</b></div>
      </div>
      <div class="receipt-body">
        <div class="form-row" style="display:flex; align-items:flex-end; margin-bottom:20px; font-size:15px;">
           <span style="width:200px; color:#555">Received with thanks from</span>
           <div class="line-container" style="flex-grow:1; border-bottom:1.5px solid #000; margin-left:10px; font-weight:bold;">${receiverName}</div>
        </div>
        <div class="form-row" style="display:flex; align-items:flex-end; margin-bottom:20px; font-size:15px;">
           <span style="width:200px; color:#555">The sum of Rupees</span>
           <div class="line-container" style="flex-grow:1; border-bottom:1.5px solid #000; margin-left:10px; font-weight:bold;">${amountInWords}</div>
        </div>
        <div class="form-row" style="display:flex; align-items:flex-end; margin-bottom:20px; font-size:15px;">
           <span style="width:200px; color:#555">On Account of</span>
           <div class="line-container" style="flex-grow:1; border-bottom:1.5px solid #000; margin-left:10px; font-weight:bold;">${shipmentResult.trackingNumber}</div>
        </div>
        <div style="display:flex; justify-content:space-between; margin-bottom:20px;">
           <div class="form-row" style="display:flex; align-items:flex-end; flex:1; font-size:15px;">
              <span style="width:210px; color:#555">By CHEQUE/P.O./CASH/ONLINE</span>
              <div class="line-container" style="flex-grow:1; border-bottom:1.5px solid #000; margin-left:10px; font-weight:bold;">${paymentMethod || '__________'}</div>
           </div>
           <div class="form-row" style="display:flex; align-items:flex-end; width:200px; margin-left:20px; font-size:15px;">
              <span style="width:50px; color:#555">Dated</span>
              <div class="line-container" style="flex-grow:1; border-bottom:1.5px solid #000; margin-left:10px; font-weight:bold;">${today}</div>
           </div>
        </div>
        <div class="form-row" style="display:flex; align-items:flex-end; margin-bottom:20px; font-size:15px;">
           <span style="width:100px; color:#555">Drawn on</span>
           <div class="line-container" style="flex-grow:1; border-bottom:1.5px solid #000; margin-left:10px; font-weight:bold;">__________</div>
        </div>
        <div style="display:flex; justify-content:space-between; align-items:center; margin-top:20px;">
          <div style="display:flex; align-items:center;">
             <span style="font-weight:bold; font-size:18px">Rs.</span>
             <span style="font-family:monospace; font-weight:bold; font-size:24px; border:2px solid #000; padding:5px 20px; margin-left:10px; background:#f9f9f9">${formattedTotalAmount}</span>
          </div>
          <div style="flex-grow:1; margin-left:30px;">
             <div class="form-row" style="display:flex; align-items:flex-end; margin-bottom:0; font-size:14px;">
                <span style="width:150px; color:#555">Tnx ID / Check No.*:</span>
                <div class="line-container" style="flex-grow:1; border-bottom:1.5px solid #000; margin-left:10px; font-weight:bold;">${transactionId || '__________'}</div>
             </div>
          </div>
        </div>
        
        <div style="margin-top:35px; border:1px solid #ddd; padding:15px; border-radius:8px; display:flex; justify-content:space-around; background:#fcfcfc">
          <div style="text-align:center;">
            <div style="font-weight:bold; font-size:18px; color:#222;">${packages.length}</div>
            <div style="font-size:11px; color:#666; text-transform:uppercase; letter-spacing:0.5px">Number of Packages</div>
          </div>
          <div style="text-align:center; border-left:1px solid #eee; border-right:1px solid #eee; padding:0 30px">
            <div style="font-weight:bold; font-size:18px; color:#222;">${totalBillableWeight} kg</div>
            <div style="font-size:11px; color:#666; text-transform:uppercase; letter-spacing:0.5px">Total Weight</div>
          </div>
          <div style="text-align:center;">
            <div style="font-weight:bold; font-size:18px; color:#222;">${receiptDestinationCountry || 'N/A'}</div>
            <div style="font-size:11px; color:#666; text-transform:uppercase; letter-spacing:0.5px">Country of Destination</div>
          </div>
        </div>
      </div>
      <div style="margin-top: 30px; font-size: 13px; font-style: italic; color: #444;">Generated by: <b>${generatedBy}</b></div>
      <div class="receipt-footer" style="margin-top: 50px; display: flex; justify-content: space-between; align-items: flex-end;">
        <div style="font-style:italic; font-size:12px">*SUBJECT TO REALIZATION</div>
        <div style="text-align:right; font-weight:bold; font-size:13px; line-height:1.4">
          Universal Logistics Services (Pvt.) Ltd.<br/>
          Authorised Service Contractor for UPS.
        </div>
      </div>
    </div>
  </div>
  ` : ''}
  
  <script>
    window.onload = function() {
      setTimeout(() => {
        window.print();
        // Optional: window.close();
      }, 500);
    };
  </script>
  ${(includeCommercialInvoice && !shipmentResult.commercialInvoice) ? `
  <div class="page">
    <div style="padding: 20px;">
      ${generateCommercialInvoiceHTML({
                shipper,
                destination,
                products,
                commodityInvoiceNo: commodityInvoiceNo,
                freightValue: freightValue,
                exportReason: exportReason,
                countryOfOrigin: getCountryName(products.find(p => p.countryOfOrigin)?.countryOfOrigin || countryOfOrigin || shipper.country || origin.country) || countryOfOrigin || '',
                declarationStatement: declarationStatement,
                currencyType: currencyType,
                date: today,
                trackingNumber: shipmentResult.trackingNumber,
                iossNumber: iossNumber,
                insuranceValue: (() => {
                    const decVal = parseFloat(shipmentInsuranceDeclaredValue) || 0;
                    return decVal > 100 ? Math.max(6.60, decVal * 0.01) : 0;
                })(),
                shipmentInsuranceDeclaredValue: shipmentInsuranceDeclaredValue
            })}
    </div>
  </div>
  ` : ''}
</body>
</html>`;
            win.document.write(html);
            win.document.close();
        };

        return (
            <div className="shipping-container">
                <div className="shipping-header">
                    <button className="back-btn" onClick={onBack}>← Back</button>
                    <h2>Shipment Created</h2>
                </div>

                <div className="success-card">
                    <div className="success-left">
                        <div className="success-badge">✓</div>
                        <div className="success-info">
                            <div className="success-title">Shipment Created Successfully</div>
                            <div className="tracking-number">{shipmentResult.trackingNumber}</div>
                            <div className="meta">Service: {shipmentResult.service || 'UPS Saver (65)'}{isDDP ? ' • DDP' : ''}{noCommercialValue ? ' • DOC' : ''}</div>
                            <div className="meta">Total Charges: {shipmentResult.currency} {parseFloat(shipmentResult.totalCharges || 0).toFixed(2)}</div>
                        </div>
                    </div>

                    <div className="success-right">
                        {labels.length > 0 && (
                            <div className="label-thumbs">
                                {labels.map((l, idx) => (
                                    <img key={idx} src={`data:image/gif;base64,${l}`} alt={`Label ${idx + 1}`} />
                                ))}
                            </div>
                        )}

                        <div className="actions-row">
                            <button className="btn btn-primary" onClick={downloadAllAsPDF} style={{ background: '#2c5282' }}>(Labels + Receipt)</button>
                            <button className="btn btn-outline" onClick={printLabels}>Print Label (A4)</button>
                            {shipmentResult.commercialInvoice && (
                                <button className="btn btn-primary" onClick={viewUPSCommercialInvoice} style={{ background: '#276749' }}>📄Commercial Invoice</button>
                            )}
                            {isSuperUser && !companySlug && (
                                <CreateReceipt
                                    receiptNo={shipmentResult.invoiceNo}
                                    invoiceNo={shipmentResult.invoiceNo}
                                    trackingNumber={shipmentResult.trackingNumber}
                                    amountPKR={accountRates[selectedAccount] && accountRates[selectedAccount].breakdown ? (accountRates[selectedAccount].breakdown.grandTotalWithDDPPKR || accountRates[selectedAccount].breakdown.grandTotalPKR || accountRates[selectedAccount].breakdown.totalPKR) : null}
                                    receiverName={destination.name || destination.company || destination.attention}
                                    createdBy={currentUser || 'system'}
                                    paymentMethod={isPaymentReceived ? paymentMethod : ''}
                                    transactionId={isPaymentReceived ? transactionId : ''}
                                    accountNo={selectedAccount}
                                    receiptEnabled={companySettings.receiptEnabled}
                                    packages={packages}
                                    countryOfOrigin={getCountryName(destination.country)}
                                    totalWeight={packages.reduce((sum, pkg) => sum + getBillableWeight(pkg), 0).toFixed(2)}
                                />
                            )}
                            <button className="btn btn-outline" onClick={downloadLabels}>Download Label Image(s)</button>
                            <button className="btn btn-ghost" onClick={resetForm}>+ Create Another Shipment</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="shipping-container">
            {view === 'create' ? (
                <div className="shipping-header">
                    <button className="back-btn" onClick={() => setView('list')}>← Back to Shipments</button>
                    <h2>📦 Create Shipment (UPS Saver)</h2>
                </div>
            ) : (
                <div className="shipping-header" style={{ justifyContent: 'space-between', marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>
                        <h2>📦 Shipment History</h2>
                    </div>
                    <button className="action-btn primary" style={{ width: 'auto', padding: '0.75rem 1.5rem', marginTop: 0 }} onClick={() => setView('create')}>+ Create New Shipment</button>
                </div>
            )}

            {error && (
                <div className="message-banner error" style={{ marginBottom: '1rem' }}>
                    <div>{error}</div>
                    {debugSteps && debugSteps.length > 0 && (
                        <div style={{
                            marginTop: '0.75rem',
                            paddingTop: '0.75rem',
                            borderTop: '1px solid rgba(255,255,255,0.3)',
                            fontSize: '0.85rem'
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>🔍 Lookup Details:</div>
                            {debugSteps.map((step, idx) => (
                                <div key={idx} style={{ marginBottom: '0.5rem', marginLeft: '0.5rem' }}>
                                    <div>{step.action}</div>
                                    {step.message && <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>→ {step.message}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {success && (
                <div style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    background: '#c6f6d5',
                    border: '1px solid #9ae6b4',
                    borderRadius: '8px',
                    color: '#22543d',
                    fontWeight: '500',
                    fontSize: '0.95rem',
                    animation: 'slideDown 0.3s ease'
                }}>
                    {success}
                </div>
            )}

            {view === 'create' && (
                <div className="shipping-main">
                    <div className="shipping-form">

                        {/* Account Selection - Compact */}
                        <div className="form-section compact-section">
                            <div className="section-title small-title">1. Product Type</div>
                            <div className="form-row compact-row">
                                <div className="form-group compact-group">
                                    <label>Product Type</label>
                                    <select
                                        value={productType}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setProductType(val);
                                            if (val === 'DOCUMENTS' || val === 'LETTERS') {
                                                setNoCommercialValue(true);
                                                setIsDDP(false);
                                                setDdpTransportationPayer('BillShipper');
                                                setDdpDutyTaxPayer('BillShipper');
                                            } else {
                                                setNoCommercialValue(false);
                                            }
                                        }}
                                        className="compact-input"
                                    >
                                        <option value="NON-DOCUMENTS">Non-Documents</option>
                                        <option value="10KG PRODUCT BOX">10KG Product Box</option>
                                        <option value="25KG PRODUCT BOX">25KG Product Box</option>
                                        <option value="DOCUMENTS">Documents</option>
                                        <option value="LETTERS">Letters</option>
                                    </select>
                                </div>
                                {productType === 'NON-DOCUMENTS' && (
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                        <button
                                            className="action-btn-blue"
                                            style={{ flex: 1, padding: '4px 8px', fontSize: '0.8rem', background: '#3182ce' }}
                                            onClick={() => setProductType('10KG PRODUCT BOX')}
                                        >
                                            📦 10KG Product Box
                                        </button>
                                        <button
                                            className="action-btn-blue"
                                            style={{ flex: 1, padding: '4px 8px', fontSize: '0.8rem', background: '#2c5282' }}
                                            onClick={() => setProductType('25KG PRODUCT BOX')}
                                        >
                                            📦 25KG Product Box
                                        </button>
                                    </div>
                                )}
                                {(productType === '10KG PRODUCT BOX' || productType === '25KG PRODUCT BOX') && (
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                        <button
                                            className="action-btn-gray"
                                            style={{ flex: 1, padding: '4px 8px', fontSize: '0.8rem', background: '#718096', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                            onClick={() => setProductType('NON-DOCUMENTS')}
                                        >
                                            ↩️ Back to Standard Non-Docs
                                        </button>
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                                    <label style={{ fontSize: '0.85rem', color: '#4a5568', minWidth: '90px' }}>Sales Tax</label>
                                    <select value={taxSelection} onChange={(e) => setTaxSelection(e.target.value)} style={{ padding: '6px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                        <option value="auto">Auto (based on origin)</option>
                                        <option value="15">GST (15%)</option>
                                        <option value="16">Punjab GST (16%)</option>
                                        <option value="fixed">Fixed Tax (1000 PKR)</option>
                                        <option value="0">No GST (0%)</option>
                                    </select>
                                    <div style={{ fontSize: '0.75rem', color: '#718096' }}>Choose tax to apply in rate/billing breakdown</div>
                                </div>
                            </div>

                        </div>

                        {/* 3 & 4. Address Split Container */}
                        <div className="address-split-container">


                            {/* FROM SECTION */}
                            <div className="address-column">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3>From</h3>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <select
                                            className="premium-input"
                                            style={{ width: 'auto', minWidth: '130px', maxWidth: '280px', height: '30px', padding: '2px 8px' }}
                                            value={selectedShipperId}
                                            onChange={(e) => {
                                                setSelectedShipperId(e.target.value);
                                                const addr = savedAddresses.find(a => a._id === e.target.value);
                                                if (addr) setShipper({
                                                    ...shipper,
                                                    name: addr.name,
                                                    company: addr.company || '',
                                                    attention: addr.attention || '',
                                                    phone: addr.phone || '',
                                                    email: addr.email || '',
                                                    address1: addr.address1,
                                                    address2: addr.address2 || '',
                                                    address3: addr.address3 || '',
                                                    city: addr.city,
                                                    state: addr.state || '',
                                                    postalCode: addr.postalCode || '',
                                                    country: addr.country,
                                                    taxId: addr.taxId || ''
                                                });
                                            }}
                                        >
                                            <option value="">Load Address...</option>
                                            {savedAddresses.filter(a => a.type === 'shipper').map(a => (
                                                <option key={a._id} value={a._id}>{formatAddressOption(a)}</option>
                                            ))}
                                        </select>
                                        {selectedShipperId && (
                                            <button
                                                title="Delete this saved address"
                                                onClick={async () => { await handleDeleteAddress(selectedShipperId); setSelectedShipperId(''); }}
                                                style={{ height: '30px', padding: '0 8px', background: '#fff5f5', border: '1px solid #fc8181', borderRadius: '4px', color: '#e53e3e', cursor: 'pointer', fontSize: '0.85rem' }}
                                            >🗑️</button>
                                        )}
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className={getFieldGroupClass('originName')}>
                                        <label>Name <span className="required-mark">*</span></label>
                                        <div className="input-with-icon">
                                            <input className="premium-input" type="text" value={shipper.name} onChange={(e) => { setShipper({ ...shipper, name: e.target.value }); setFieldErrors(prev => ({ ...prev, originName: false })); }} />
                                            <button className="icon-btn-yellow" onClick={() => handleSaveAddress('shipper', shipper)}>
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Type</label>
                                        <select className="premium-input" value={shipper.type} onChange={(e) => setShipper({ ...shipper, type: e.target.value })}>
                                            <option value="01">Business (01)</option>
                                            <option value="02">Consumer (02)</option>

                                        </select>
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Company</label>
                                        <input className="premium-input" type="text" value={shipper.company || ''} onChange={(e) => setShipper({ ...shipper, company: e.target.value })} />
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className={getFieldGroupClass('originCountry')}>
                                        <label>Country/Territory <span className="required-mark">*</span></label>
                                        <select className="premium-input" value={shipper.country} onChange={(e) => { setShipper({ ...shipper, country: e.target.value }); setFieldErrors(prev => ({ ...prev, originCountry: false })); }}>
                                            {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className={getFieldGroupClass('originAddress1')}>
                                        <label>Address 1 <span className="required-mark">*</span></label>
                                        <input className="premium-input" type="text" value={shipper.address1} onChange={(e) => { setShipper({ ...shipper, address1: e.target.value }); setFieldErrors(prev => ({ ...prev, originAddress1: false })); }} />
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Address 2</label>
                                        <input className="premium-input" type="text" value={shipper.address2} onChange={(e) => setShipper({ ...shipper, address2: e.target.value })} placeholder="Apartment, suite, unit, etc." />
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Address 3</label>
                                        <input className="premium-input" type="text" value={shipper.address3} onChange={(e) => setShipper({ ...shipper, address3: e.target.value })} placeholder="Department, floor, etc." />
                                    </div>
                                </div>

                                <div className="city-zip-state-row">
                                    <div className="field-group">
                                        <label>Postal Code</label>
                                        <input className="premium-input" type="text" value={shipper.postalCode} onChange={(e) => setShipper({ ...shipper, postalCode: e.target.value })} />
                                    </div>
                                    <div className={getFieldGroupClass('originCity')}>
                                        <label>City <span className="required-mark">*</span></label>
                                        <input className="premium-input" type="text" value={shipper.city} onChange={(e) => { setShipper({ ...shipper, city: e.target.value }); setFieldErrors(prev => ({ ...prev, originCity: false })); }} />
                                    </div>
                                    <div className="field-group">
                                        <label>State</label>
                                        <input className="premium-input" type="text" value={shipper.state} onChange={(e) => setShipper({ ...shipper, state: e.target.value })} />
                                    </div>
                                </div>

                                <div className="checkbox-row">
                                    <input type="checkbox" id="from-res" checked={shipper.isResidential} onChange={(e) => setShipper({ ...shipper, isResidential: e.target.checked })} />
                                    <label htmlFor="from-res">Residential Address</label>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Email Address</label>
                                        <input className="premium-input" type="email" value={shipper.email} onChange={(e) => setShipper({ ...shipper, email: e.target.value })} />
                                    </div>
                                </div>

                                <div className="phone-row">
                                    <div className="field-group">
                                        <label>Phone Type</label>
                                        <select className="premium-input">
                                            <option>Work</option>
                                            <option>Mobile</option>
                                            <option>Home</option>
                                        </select>
                                    </div>
                                    <div className="field-group">
                                        <label>Code</label>
                                        <input className="premium-input" type="text" value={shipper.country === 'PK' ? '92' : '1'} readOnly />
                                    </div>
                                    <div className="field-group">
                                        <label>Phone</label>
                                        <input className="premium-input" type="text" value={shipper.phone} onChange={(e) => setShipper({ ...shipper, phone: e.target.value })} />
                                    </div>
                                </div>

                                <div className="checkbox-row" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', background: shipperNotify.enabled ? '#ebf8ff' : 'transparent', padding: '4px 8px', borderRadius: '4px', border: shipperNotify.enabled ? '1px solid #90cdf4' : 'none' }}>
                                        <input type="checkbox" id="from-email-notify" checked={shipperNotify.enabled} onChange={(e) => setShipperNotify({ ...shipperNotify, enabled: e.target.checked })} />
                                        <label htmlFor="from-email-notify" style={{ fontWeight: shipperNotify.enabled ? 'bold' : 'normal', color: shipperNotify.enabled ? '#2b6cb0' : 'inherit', marginLeft: '5px' }}>Email Notifications</label>
                                    </div>
                                    {shipperNotify.enabled && (
                                        <div style={{ display: 'flex', gap: '10px', marginLeft: '25px', marginTop: '5px', fontSize: '0.85rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={shipperNotify.ship} onChange={(e) => setShipperNotify({ ...shipperNotify, ship: e.target.checked })} /> Creation</label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={shipperNotify.exception} onChange={(e) => setShipperNotify({ ...shipperNotify, exception: e.target.checked })} /> Exception</label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={shipperNotify.delivery} onChange={(e) => setShipperNotify({ ...shipperNotify, delivery: e.target.checked })} /> Delivery</label>
                                        </div>
                                    )}
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>G/L Number</label>
                                        <input
                                            className="premium-input"
                                            type="text"
                                            value={autoGL || userGL || shipper.account || ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setShipper({ ...shipper, account: val });
                                                setOrigin(prev => ({ ...prev, account: val }));
                                            }}
                                            disabled={!!companySlug || !!userGL || !!autoGL}
                                            style={(companySlug || userGL || autoGL) ? { backgroundColor: '#f7fafc' } : {}}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="address-divider"></div>

                            {/* TO SECTION */}
                            <div className="address-column">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3>To</h3>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <select
                                            className="premium-input"
                                            style={{ width: 'auto', minWidth: '130px', maxWidth: '280px', height: '30px', padding: '2px 8px' }}
                                            value={selectedDestId}
                                            onChange={(e) => {
                                                setSelectedDestId(e.target.value);
                                                const addr = savedAddresses.find(a => a._id === e.target.value);
                                                if (addr) setDestination({
                                                    ...destination,
                                                    name: addr.name,
                                                    company: addr.company || '',
                                                    attention: addr.attention || '',
                                                    phone: addr.phone || '',
                                                    email: addr.email || '',
                                                    address1: addr.address1,
                                                    address2: addr.address2 || '',
                                                    address3: addr.address3 || '',
                                                    city: addr.city,
                                                    state: addr.state || '',
                                                    postalCode: addr.postalCode || '',
                                                    country: addr.country,
                                                    taxId: addr.taxId || ''
                                                });
                                            }}
                                        >
                                            <option value="">Load Address...</option>
                                            {savedAddresses.filter(a => a.type === 'destination').map(a => (
                                                <option key={a._id} value={a._id}>{formatAddressOption(a)}</option>
                                            ))}
                                        </select>
                                        {selectedDestId && (
                                            <button
                                                title="Delete this saved address"
                                                onClick={async () => { await handleDeleteAddress(selectedDestId); setSelectedDestId(''); }}
                                                style={{ height: '30px', padding: '0 8px', background: '#fff5f5', border: '1px solid #fc8181', borderRadius: '4px', color: '#e53e3e', cursor: 'pointer', fontSize: '0.85rem' }}
                                            >🗑️</button>
                                        )}
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className={getFieldGroupClass('destinationName')}>
                                        <label>Name <span className="required-mark">*</span></label>
                                        <div className="input-with-icon">
                                            <input className="premium-input" type="text" value={destination.name} onChange={(e) => { setDestination({ ...destination, name: e.target.value }); setFieldErrors(prev => ({ ...prev, destinationName: false })); }} />
                                            <button className="icon-btn-yellow" onClick={() => handleSaveAddress('destination', destination)}>
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Consignee Type</label>
                                        <select className="premium-input" value={destination.type} onChange={(e) => setDestination({ ...destination, type: e.target.value })}>
                                            <option value="01">Business (01)</option>
                                            <option value="02">Consumer (02)</option>

                                        </select>
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Company</label>
                                        <input className="premium-input" type="text" value={destination.company || ''} onChange={(e) => setDestination({ ...destination, company: e.target.value })} />
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className={getFieldGroupClass('destinationCountry')}>
                                        <label>Country/Territory <span className="required-mark">*</span></label>
                                        <select className="premium-input" value={destination.country} onChange={(e) => { setDestination({ ...destination, country: e.target.value }); setFieldErrors(prev => ({ ...prev, destinationCountry: false })); }}>
                                            {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className={getFieldGroupClass('destinationAddress1')}>
                                        <label>Address 1 <span className="required-mark">*</span></label>
                                        <input className="premium-input" type="text" value={destination.address1} onChange={(e) => { setDestination({ ...destination, address1: e.target.value }); setFieldErrors(prev => ({ ...prev, destinationAddress1: false })); }} />
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Address 2</label>
                                        <input className="premium-input" type="text" value={destination.address2} onChange={(e) => setDestination({ ...destination, address2: e.target.value })} placeholder="Apartment, suite, etc." />
                                    </div>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Address 3</label>
                                        <input className="premium-input" type="text" value={destination.address3} onChange={(e) => setDestination({ ...destination, address3: e.target.value })} placeholder="Department, floor, etc." />
                                    </div>
                                </div>

                                <div className="city-zip-state-row">
                                    <div className="field-group">
                                        <label>Postal Code</label>
                                        <input className="premium-input" type="text" value={destination.postalCode} onChange={(e) => setDestination({ ...destination, postalCode: e.target.value })} />
                                    </div>
                                    <div className={getFieldGroupClass('destinationCity')}>
                                        <label>City <span className="required-mark">*</span></label>
                                        <input className="premium-input" type="text" value={destination.city} onChange={(e) => { setDestination({ ...destination, city: e.target.value }); setFieldErrors(prev => ({ ...prev, destinationCity: false })); }} />
                                    </div>
                                    <div className="field-group">
                                        <label>State</label>
                                        <input className="premium-input" type="text" value={destination.state} onChange={(e) => setDestination({ ...destination, state: e.target.value })} />
                                    </div>
                                </div>

                                <div className="checkbox-row">
                                    <input type="checkbox" id="to-res" checked={destination.isResidential} onChange={(e) => setDestination({ ...destination, isResidential: e.target.checked })} />
                                    <label htmlFor="to-res">Residential Address</label>
                                </div>

                                <div className="field-row">
                                    <div className="field-group">
                                        <label>Email Address</label>
                                        <input className="premium-input" type="email" value={destination.email} onChange={(e) => setDestination({ ...destination, email: e.target.value })} />
                                    </div>
                                </div>

                                <div className="phone-row">
                                    <div className="field-group">
                                        <label>Phone Type</label>
                                        <select className="premium-input">
                                            <option>Work</option>
                                            <option>Mobile</option>
                                            <option>Home</option>
                                        </select>
                                    </div>
                                    <div className="field-group">
                                        <label>Code</label>
                                        <input className="premium-input" type="text" value={destination.country === 'PK' ? '92' : '1'} readOnly />
                                    </div>
                                    <div className="field-group">
                                        <label>Phone</label>
                                        <input className="premium-input" type="text" value={destination.phone} onChange={(e) => setDestination({ ...destination, phone: e.target.value })} />
                                    </div>
                                </div>

                                <div className="checkbox-row" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', background: receiverNotify.enabled ? '#ebf8ff' : 'transparent', padding: '4px 8px', borderRadius: '4px', border: receiverNotify.enabled ? '1px solid #90cdf4' : 'none' }}>
                                        <input type="checkbox" id="to-email-notify" checked={receiverNotify.enabled} onChange={(e) => setReceiverNotify({ ...receiverNotify, enabled: e.target.checked })} />
                                        <label htmlFor="to-email-notify" style={{ fontWeight: receiverNotify.enabled ? 'bold' : 'normal', color: receiverNotify.enabled ? '#2b6cb0' : 'inherit', marginLeft: '5px' }}>Email Notifications</label>
                                    </div>
                                    {receiverNotify.enabled && (
                                        <div style={{ display: 'flex', gap: '10px', marginLeft: '25px', marginTop: '5px', fontSize: '0.85rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={receiverNotify.ship} onChange={(e) => setReceiverNotify({ ...receiverNotify, ship: e.target.checked })} /> Creation</label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={receiverNotify.exception} onChange={(e) => setReceiverNotify({ ...receiverNotify, exception: e.target.checked })} /> Exception</label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={receiverNotify.delivery} onChange={(e) => setReceiverNotify({ ...receiverNotify, delivery: e.target.checked })} /> Delivery</label>
                                        </div>
                                    )}
                                </div>

                                <div className="field-row horizontal">
                                    <div className="field-group">
                                        <label>Tax ID Type</label>
                                        <select className="premium-input" value={destination.taxIdType || 'TAX ID'} onChange={(e) => setDestination({ ...destination, taxIdType: e.target.value })}>
                                            <option value="TAX ID">TAX ID</option>
                                            <option value="VAT">VAT</option>
                                            <option value="EORI">EORI</option>
                                            <option value="CPF">CPF</option>
                                            <option value="CNPJ">CNPJ</option>
                                            <option value="EIN">EIN</option>
                                        </select>
                                    </div>
                                    <div className="field-group">
                                        <label>Tax ID Value</label>
                                        <input className="premium-input" type="text" value={destination.taxId || ''} onChange={(e) => setDestination({ ...destination, taxId: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Billing Selection Split Container */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '25px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    alignItems: 'stretch',
                                    background: '#f8fafc',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0'
                                }}
                            >
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {/* ENABLE DDP */}
                                    <div className="ddp-toggle compact-toggle" style={{ flex: 1, border: isDDP ? '1px solid #c53030' : '1px solid #e2e8f0', background: isDDP ? '#fff5f5' : '#f8fafc' }}>
                                        <input
                                            type="checkbox"
                                            id="ddp-check"
                                            checked={isDDP}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (checked) {
                                                    setIsDDP(true);
                                                    setDdpTransportationPayer('BillShipper');
                                                    setDdpDutyTaxPayer('BillShipper');
                                                    setNoCommercialValue(false);
                                                } else {
                                                    setIsDDP(false);
                                                }
                                            }}
                                        />
                                        <label htmlFor="ddp-check" style={{ fontSize: '0.75rem', fontWeight: 'bold', color: isDDP ? '#c53030' : '#4a5568' }}>ENABLE DDP</label>
                                    </div>

                                    {/* BILL THIRD PARTY */}
                                    <div className="ddp-toggle compact-toggle" style={{ flex: 1, border: ddpTransportationPayer === 'BillThirdParty' ? '1px solid #2b6cb0' : '1px solid #e2e8f0', background: ddpTransportationPayer === 'BillThirdParty' ? '#ebf8ff' : '#f8fafc' }}>
                                        <input
                                            type="checkbox"
                                            id="third-party-check"
                                            checked={ddpTransportationPayer === 'BillThirdParty'}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (checked) {
                                                    setDdpTransportationPayer('BillThirdParty');
                                                    setIsDDP(false);
                                                    setDdpDutyTaxPayer('BillShipper');
                                                    setNoCommercialValue(false);
                                                    setShowThirdPartyModal(true);
                                                } else {
                                                    setDdpTransportationPayer('BillShipper');
                                                }
                                            }}
                                        />
                                        <label htmlFor="third-party-check" style={{ fontSize: '0.75rem', fontWeight: 'bold', color: ddpTransportationPayer === 'BillThirdParty' ? '#2b6cb0' : '#4a5568' }}>BILL 3RD PARTY</label>
                                    </div>

                                    {/* DUTY/TAX TO RECEIVER */}
                                    <div className="ddp-toggle compact-toggle" style={{ flex: 1.2, border: ddpDutyTaxPayer === 'BillReceiver' ? '1px solid #38a169' : '1px solid #e2e8f0', background: ddpDutyTaxPayer === 'BillReceiver' ? '#f0fff4' : '#f8fafc' }}>
                                        <input
                                            type="checkbox"
                                            id="duty-receiver-check"
                                            checked={ddpDutyTaxPayer === 'BillReceiver'}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (checked) {
                                                    setDdpDutyTaxPayer('BillReceiver');
                                                    setIsDDP(false);
                                                    setDdpTransportationPayer('BillShipper');
                                                    setNoCommercialValue(false);
                                                } else {
                                                    setDdpDutyTaxPayer('BillShipper');
                                                }
                                            }}
                                        />
                                        <label htmlFor="duty-receiver-check" style={{ fontSize: '0.75rem', fontWeight: 'bold', color: ddpDutyTaxPayer === 'BillReceiver' ? '#38a169' : '#4a5568' }}>DUTY/TAX TO REC.</label>
                                    </div>

                                    {/* NO COMMERCIAL VALUE */}
                                    <div className="ddp-toggle compact-toggle" style={{ flex: 1.2, border: noCommercialValue ? '1px solid #718096' : '1px solid #e2e8f0', background: noCommercialValue ? '#edf2f7' : '#f8fafc' }}>
                                        <input
                                            type="checkbox"
                                            id="no-value-check"
                                            checked={noCommercialValue}
                                            disabled={productType === 'LETTERS' || productType === 'DOCUMENTS'}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (checked) {
                                                    setNoCommercialValue(true);
                                                    setIsDDP(false);
                                                    setDdpTransportationPayer('BillShipper');
                                                    setDdpDutyTaxPayer('BillShipper');
                                                } else {
                                                    setNoCommercialValue(false);
                                                }
                                            }}
                                        />
                                        <label htmlFor="no-value-check" style={{ fontSize: '0.75rem', fontWeight: 'bold', color: noCommercialValue ? '#4a5568' : '#718096' }}>DOCS (NO VALUE)</label>
                                    </div>
                                </div>

                                {/* Conditionally reveal inputs for specific settings */}
                                {ddpTransportationPayer === 'BillThirdParty' && (
                                    <div onClick={() => setShowThirdPartyModal(true)} style={{ marginTop: '8px', padding: '8px', background: '#ebf8ff', borderRadius: '4px', border: '1px dashed #2b6cb0', cursor: 'pointer', textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#2b6cb0' }}>
                                            {ddpThirdPartyInfo.account ? `TPR: ${ddpThirdPartyInfo.account} (${ddpThirdPartyInfo.country}) - Click to Edit` : '⚠️ Configure Third Party Address'}
                                        </div>
                                    </div>
                                )}

                                {ddpDutyTaxPayer === 'BillReceiver' && (
                                    <div style={{ marginTop: '8px', padding: '8px', background: '#f0fff4', borderRadius: '4px', border: '1px solid #38a169' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                            <div className={fieldErrors.ddpReceiverAccount ? 'form-group premium-group error' : 'form-group premium-group'}>
                                                <label style={{ fontSize: '0.65rem' }}>Rec. Account # <span style={{ color: 'red' }}>*</span></label>
                                                <input className="premium-input" style={{ height: '30px' }} value={ddpReceiverInfo.account} onChange={e => { setDdpReceiverInfo({ ...ddpReceiverInfo, account: e.target.value.toUpperCase() }); setFieldErrors(prev => ({ ...prev, ddpReceiverAccount: false })); }} placeholder="Account" />
                                            </div>
                                            <div className={fieldErrors.ddpReceiverPostalCode ? 'form-group premium-group error' : 'form-group premium-group'}>
                                                <label style={{ fontSize: '0.65rem' }}>Postal Code <span style={{ color: 'red' }}>*</span></label>
                                                <input className="premium-input" style={{ height: '30px' }} value={ddpReceiverInfo.postalCode} onChange={e => { setDdpReceiverInfo({ ...ddpReceiverInfo, postalCode: e.target.value }); setFieldErrors(prev => ({ ...prev, ddpReceiverPostalCode: false })); }} placeholder="Zip" />
                                            </div>
                                            <div className={fieldErrors.ddpReceiverCountry ? 'form-group premium-group error' : 'form-group premium-group'}>
                                                <label style={{ fontSize: '0.65rem' }}>Country <span style={{ color: 'red' }}>*</span></label>
                                                <select className="premium-input" value={ddpReceiverInfo.country} onChange={e => { setDdpReceiverInfo({ ...ddpReceiverInfo, country: e.target.value }); setFieldErrors(prev => ({ ...prev, ddpReceiverCountry: false })); }} style={{ height: '30px' }}>
                                                    {countries.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {isDDP && (!includeCommercialInvoice || products.some(p => !p.hsCode || !p.value)) && (
                                    <div style={{ fontSize: '0.7rem', color: '#e53e3e', marginTop: '4px', fontWeight: '500' }}>
                                        ⚠️ DDP requires product details with HS codes and values
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 5. References and Service Selection Grid */}
                        <div className="compact-grid-2" style={{ gap: '20px', marginBottom: '25px' }}>
                            {/* Reference Numbers Section */}
                            <div className="form-section compact-section" style={{ gridColumn: 'span 2' }}>
                                <div className="section-title small-title">Shipment References</div>
                                <div className="compact-grid-2">
                                    <div className="form-group compact-group">
                                        <label>Reference 1</label>
                                        <input
                                            className="compact-input"
                                            type="text"
                                            value={autoGL || ref1}
                                            onChange={(e) => setRef1(e.target.value)}
                                            placeholder="PO # / Dept #"
                                            disabled={!!companySlug || !!userGL || !!autoGL}
                                            style={{
                                                backgroundColor: (companySlug || userGL || autoGL) ? '#f7fafc' : 'inherit'
                                            }}
                                        />
                                    </div>
                                    <div className="form-group compact-group">
                                        <label>Reference 2</label>
                                        <input className="compact-input" type="text" value={ref2} onChange={(e) => setRef2(e.target.value)} placeholder="Project # / Job #" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Shipment Contents (Commodities) - Compact Table Style */}
                        <div className="form-section compact-section">
                            <div className="section-title small-title">
                                <span>5. Commodities <span style={{ fontSize: '0.8rem', fontWeight: 'normal', marginLeft: '10px' }}>(Total Val: {currencyType} {calculateTotalValue().toFixed(2)})</span></span>
                            </div>
                            <div style={{ marginTop: '1rem', padding: '0.5rem', background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '6px', width: '100%', marginBottom: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', margin: 0, fontSize: '0.9rem', fontWeight: '500' }}>
                                    <input
                                        type="checkbox"
                                        checked={includeCommercialInvoice}
                                        onChange={(e) => setIncludeCommercialInvoice(e.target.checked)}
                                        style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                    />
                                    <span>Include Product Details (Commercial Invoice)</span>
                                </label>
                                {companySlug && (
                                    <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.5rem', marginLeft: '1.75rem', fontWeight: 600 }}>
                                        ℹ️ Commercial invoice will include product/customs details. Receipts (payment records) are admin-only.
                                    </div>
                                )}
                                {!companySlug && companySettings.receiptEnabled && (
                                    <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.5rem', marginLeft: '1.75rem' }}>
                                        Check to include commercial invoice with product details
                                    </div>
                                )}
                            </div>
                            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.5rem' }}>General Description (for UPS Label)</label>
                                <input
                                    className="compact-input"
                                    type="text"
                                    value={generalDescription}
                                    onChange={(e) => setGeneralDescription(e.target.value)}
                                    placeholder="e.g. Ladies Suits"
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.5rem' }}>📦 Insurance Declared Value (USD)</label>
                                <input
                                    className="compact-input"
                                    type="number"
                                    value={shipmentInsuranceDeclaredValue}
                                    onChange={(e) => setShipmentInsuranceDeclaredValue(e.target.value)}
                                    placeholder="e.g. 500"
                                    style={{ width: '100%' }}
                                    min="0"
                                    step="0.01"
                                />
                                <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.25rem' }}>
                                    💡 Insurance Charges: ≤$100 = $0 | greater than $100 = MAX($6.60, 1% of value)
                                </div>
                            </div>
                            <div style={{ marginTop: '1rem', marginBottom: '1rem', filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1, transition: 'all 0.3s ease', pointerEvents: !includeCommercialInvoice ? 'none' : 'auto' }}>
                                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.5rem' }}>🚢 Freight Value (USD/Currency)</label>
                                <input
                                    className="compact-input"
                                    type="number"
                                    value={freightValue}
                                    onChange={(e) => setFreightValue(e.target.value)}
                                    placeholder="e.g. 150"
                                    style={{ width: '100%' }}
                                    min="0"
                                    step="0.01"
                                    disabled={!includeCommercialInvoice}
                                />
                                <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.25rem' }}>
                                    💡 Freight cost to be included in the commercial invoice total.
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', fontWeight: 'normal', filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1, transition: 'all 0.3s ease', pointerEvents: !includeCommercialInvoice ? 'none' : 'auto' }}>
                                <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Invoice #:</label>
                                <input
                                    className="compact-input"
                                    style={{ width: '120px', fontWeight: 'bold' }}
                                    type="text"
                                    placeholder="e.g. INV-001"
                                    value={commodityInvoiceNo}
                                    onChange={(e) => setCommodityInvoiceNo(e.target.value)}
                                    disabled={!includeCommercialInvoice}
                                />
                                {!companySlug && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            // Generate receipt number using company settings
                                            let newReceipt = '';
                                            if (companySettings.receiptFormat === 'custom' && companySettings.receiptPrefix) {
                                                newReceipt = `${companySettings.receiptPrefix}-${companySettings.receiptStartNumber}`;
                                            } else if (companySettings.receiptFormat === 'compact') {
                                                newReceipt = `${companySettings.receiptStartNumber}`;
                                            } else {
                                                newReceipt = `WB-${companySettings.receiptStartNumber}`;
                                            }
                                            setCommodityInvoiceNo(newReceipt);
                                        }}
                                        disabled={!includeCommercialInvoice}
                                        style={{ padding: '4px 8px', fontSize: '0.75rem', background: '#2b6cb0', color: 'white', border: 'none', borderRadius: '4px', cursor: includeCommercialInvoice ? 'pointer' : 'not-allowed', opacity: includeCommercialInvoice ? 1 : 0.5, whiteSpace: 'nowrap' }}
                                        title={`Auto-generate invoice number using format: ${companySettings.receiptFormat === 'custom' ? companySettings.receiptPrefix + '-XXXX' : (companySettings.receiptFormat === 'compact' ? 'XXXX' : 'WB-XXXX')}`}
                                    >
                                        🔄 Auto
                                    </button>
                                )}
                                <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Currency:</label>
                                <select className="compact-input" value={currencyType} onChange={(e) => setCurrencyType(e.target.value)} style={{ width: '100px' }} disabled={!includeCommercialInvoice}>
                                    <option>USD</option>
                                    <option>EUR</option>
                                    <option>GBP</option>
                                    <option>PKR</option>
                                    <option>AUD</option>
                                    <option>CAD</option>
                                    <option>GBP</option>
                                </select>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1, transition: 'all 0.3s ease', pointerEvents: !includeCommercialInvoice ? 'none' : 'auto' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem' }}>Export Reason</label>
                                    <select className="compact-input" value={exportReason} onChange={(e) => setExportReason(e.target.value)} disabled={!includeCommercialInvoice}>
                                        <option>Sale</option>
                                        <option>Sample</option>
                                        <option>Return</option>
                                        <option>Repair</option>
                                        <option>Gift</option>
                                        <option>Other</option>
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem' }}>Country Of Origin</label>
                                    <select className="compact-input" value={countryOfOrigin} onChange={(e) => setCountryOfOrigin(e.target.value)} disabled={!includeCommercialInvoice}>
                                        <option value="">-- Select --</option>
                                        {countries.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#2b6cb0', fontWeight: 'bold' }}>IOSS Number</label>
                                    <input className="compact-input" value={iossNumber} onChange={(e) => setIossNumber(e.target.value)} placeholder="e.g. IMDEU1234567" style={{ borderColor: '#90cdf4', background: '#ebf8ff' }} />
                                </div>
                                <div style={{ flex: 2 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem' }}>Declaration Statement</label>
                                    <input className="compact-input" value={declarationStatement} onChange={(e) => setDeclarationStatement(e.target.value)} disabled={!includeCommercialInvoice} />
                                </div>
                            </div>
                            <div className="compact-table-header">
                                <span style={{ flex: 3 }}>Description</span>
                                <span style={{ flex: 1.5 }}>HS Code</span>
                                <span style={{ flex: 1.2 }}>Origin</span>
                                <span style={{ flex: 1 }}>Val ({currencyType})</span>
                                <span style={{ flex: 1 }}>Qty</span>
                                <span style={{ flex: 1 }}>UOM</span>
                                <span style={{ flex: 1 }}>Wt(kg)</span>
                                <span style={{ flex: 1.5 }}>Merch ID</span>
                                <span style={{ flex: 1.5 }}>Manuf ID</span>
                                <span style={{ flex: 1.5 }}>Std ID</span>
                                <span style={{ width: '56px' }}></span>
                            </div>
                            {products.map((prod, idx) => (
                                <div key={idx} className="compact-table-row">
                                    <div style={{ flex: 3, position: 'relative' }}>
                                        <input
                                            list={`saved-products-list-${idx}`}
                                            style={{ width: '100%', filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }}
                                            className="compact-input"
                                            placeholder="Desc (type or select saved)"
                                            value={prod.description}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const n = [...products];
                                                n[idx].description = val;
                                                // Check if user selected a saved product by label
                                                const matchedSaved = savedProducts.find(sp => sp.label === val || sp.description === val);
                                                if (matchedSaved) {
                                                    n[idx].hsCode = matchedSaved.hsCode || n[idx].hsCode;
                                                    n[idx].value = matchedSaved.value || n[idx].value;
                                                    n[idx].weight = matchedSaved.weight || n[idx].weight;
                                                    n[idx].quantity = matchedSaved.quantity || n[idx].quantity;
                                                    n[idx].uom = matchedSaved.uom || n[idx].uom;
                                                    n[idx].countryOfOrigin = matchedSaved.countryOfOrigin || n[idx].countryOfOrigin;
                                                    n[idx].merchantProductId = matchedSaved.merchantProductId || n[idx].merchantProductId;
                                                    n[idx].manufactureProductId = matchedSaved.manufactureProductId || n[idx].manufactureProductId;
                                                    n[idx].standardisedProductId = matchedSaved.standardisedProductId || n[idx].standardisedProductId;
                                                    n[idx].description = matchedSaved.description || val;
                                                    n[idx]._savedProductId = matchedSaved._id;
                                                } else {
                                                    delete n[idx]._savedProductId;
                                                }
                                                setProducts(n);
                                            }}
                                            disabled={!includeCommercialInvoice}
                                        />
                                        <datalist id={`saved-products-list-${idx}`}>
                                            {savedProducts.map(sp => (
                                                <option key={sp._id} value={sp.label}>{sp.description ? `${sp.label} — ${sp.description}` : sp.label}</option>
                                            ))}
                                        </datalist>
                                    </div>
                                    <input style={{ flex: 1.5, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} className="compact-input" placeholder="HS Code" value={prod.hsCode} onChange={(e) => { const n = [...products]; n[idx].hsCode = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice} />
                                    <select style={{ flex: 1.2, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} className="compact-input" value={prod.countryOfOrigin} onChange={(e) => { const n = [...products]; n[idx].countryOfOrigin = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice}>
                                        <option value="">Origin</option>
                                        {countries.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                                    </select>
                                    <input style={{ flex: 1, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} type="number" className="compact-input" placeholder={`Val`} value={prod.value} onChange={(e) => { const n = [...products]; n[idx].value = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice} />
                                    <input style={{ flex: 1, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} type="number" className="compact-input" placeholder="Qty" value={prod.quantity} onChange={(e) => { const n = [...products]; n[idx].quantity = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice} />
                                    <select style={{ flex: 1, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} className="compact-input" value={prod.uom || 'PCS'} onChange={(e) => { const n = [...products]; n[idx].uom = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice}>
                                        <option value="PCS">PCS</option>
                                        <option value="BOX">BOX</option>
                                        <option value="DOZ">DOZ</option>
                                        <option value="EA">EA</option>
                                        <option value="KGS">KGS</option>
                                        <option value="LBS">LBS</option>
                                        <option value="SET">SET</option>
                                        <option value="PAIRS">PAIRS</option>
                                    </select>
                                    <input style={{ flex: 1, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} type="number" className="compact-input" placeholder="Wt" value={prod.weight} onChange={(e) => { const n = [...products]; n[idx].weight = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice} />
                                    <input style={{ flex: 1.5, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} className="compact-input" placeholder="Merch ID" value={prod.merchantProductId || ''} onChange={(e) => { const n = [...products]; n[idx].merchantProductId = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice} />
                                    <input style={{ flex: 1.5, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} className="compact-input" placeholder="Manuf ID" value={prod.manufactureProductId || ''} onChange={(e) => { const n = [...products]; n[idx].manufactureProductId = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice} />
                                    <input style={{ flex: 1.5, filter: !includeCommercialInvoice ? 'blur(3px)' : 'none', opacity: !includeCommercialInvoice ? 0.6 : 1 }} className="compact-input" placeholder="Std ID" value={prod.standardisedProductId || ''} onChange={(e) => { const n = [...products]; n[idx].standardisedProductId = e.target.value; setProducts(n); }} disabled={!includeCommercialInvoice} />
                                    <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                                        <button
                                            className="icon-btn-yellow"
                                            title="Save this product to your product book"
                                            onClick={() => handleSaveProduct(prod)}
                                            style={{ width: '24px', height: '24px', padding: '2px' }}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                                        </button>
                                        {prod._savedProductId && (
                                            <button
                                                title="Delete this saved product from your book"
                                                onClick={async () => {
                                                    await handleDeleteSavedProduct(prod._savedProductId);
                                                    const n = [...products];
                                                    delete n[idx]._savedProductId;
                                                    setProducts(n);
                                                }}
                                                style={{ width: '24px', height: '24px', padding: '2px', background: '#fff5f5', border: '1px solid #fc8181', borderRadius: '4px', color: '#e53e3e', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >🗑️</button>
                                        )}
                                        <button className="remove-btn-compact" onClick={() => handleRemoveProduct(idx)}>×</button>
                                    </div>
                                </div>
                            ))}
                            <button className="add-btn-compact" onClick={handleAddProduct}>+ Add</button>
                        </div>


                        {/* Packages - Compact Table Style */}
                        <div className="form-section compact-section">
                            <div className="section-title small-title">
                                6. Packages <span style={{ fontSize: '0.8rem', fontWeight: 'normal', marginLeft: '10px' }}>(Total Wt: {calculateTotalWeight().toFixed(2)} kg)</span>
                            </div>
                            <div className="compact-table-header">
                                <span style={{ flex: 1 }}>Weight (kg)</span>
                                <span style={{ flex: 1 }}>Length</span>
                                <span style={{ flex: 1 }}>Width</span>
                                <span style={{ flex: 1 }}>Height</span>
                                <span style={{ width: '24px' }}></span>
                            </div>
                            {packages.map((pkg, idx) => (
                                <div key={idx} style={{ marginBottom: '0.45rem', paddingBottom: '0.35rem', borderBottom: idx < packages.length - 1 ? '1px dashed #cbd5e1' : 'none' }}>
                                    <div className="compact-table-row">
                                        <input style={{ flex: 1 }} type="number" className="compact-input" placeholder="Weight" value={pkg.weight} onChange={(e) => { const n = [...packages]; n[idx].weight = e.target.value; setPackages(n); }} />
                                        <input style={{ flex: 1 }} className="compact-input" placeholder="L" value={pkg.length} onChange={(e) => { const n = [...packages]; n[idx].length = e.target.value; setPackages(n); }} />
                                        <input style={{ flex: 1 }} className="compact-input" placeholder="W" value={pkg.width} onChange={(e) => { const n = [...packages]; n[idx].width = e.target.value; setPackages(n); }} />
                                        <input style={{ flex: 1 }} className="compact-input" placeholder="H" value={pkg.height} onChange={(e) => { const n = [...packages]; n[idx].height = e.target.value; setPackages(n); }} />
                                        <button className="remove-btn-compact" onClick={() => handleRemovePackage(idx)}>×</button>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.25rem', fontSize: '0.68rem', padding: '0 0.25rem' }}>
                                        <div style={{ display: 'flex', gap: '0.5rem', color: '#4a5568' }}>
                                            <span>📏 Dim Wt: <span style={{ fontWeight: 'bold', color: '#2b6cb0' }}>{calculateVolumetricWeight(pkg)} kg</span></span>
                                            <span>• 📦 Billable: <span style={{ fontWeight: 'bold', color: '#e53e3e' }}>{getBillableWeight(pkg)} kg</span></span>
                                        </div>
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: '#1e293b', fontWeight: 600 }}>
                                            <input
                                                type="checkbox"
                                                checked={!!pkg.additionalHandling}
                                                onChange={(e) => {
                                                    const n = [...packages];
                                                    n[idx].additionalHandling = e.target.checked;
                                                    setPackages(n);
                                                }}
                                                style={{ width: '13px', height: '13px', accentColor: '#2563eb', cursor: 'pointer' }}
                                            />
                                            Additional Handling / Irregular (AHC)
                                        </label>
                                    </div>
                                </div>
                            ))}
                            <button className="add-btn-compact" onClick={handleAddPackage}>+ Add</button>

                            {/* Save as Draft Button - After Packages */}
                            <button
                                className="action-btn"
                                onClick={handleSaveDraft}
                                disabled={loading}
                                style={{
                                    marginTop: '1.5rem',
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    background: '#f6ad55',
                                    color: '#7c2d12',
                                    opacity: loading ? 0.7 : 1,
                                    cursor: loading ? 'default' : 'pointer',
                                    border: '1px solid #daa520'
                                }}
                            >
                                {loading ? '⏳ Saving Draft...' : '💾 Save as Draft'}
                            </button>
                        </div>



                    </div>

                    <div className="shipping-sidebar">
                        <div className="shipping-summary">
                            {/* Account Cards View */}
                            {expandedAccountId === null ? (
                                <div>
                                    <div className="summary-title">💳 Select Account</div>
                                    {accounts.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: '#a0aec0', padding: '2rem 0', fontSize: '0.9rem' }}>
                                            No accounts available. Configure in Settings.
                                        </div>
                                    ) : (
                                        <div className="account-cards-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem', maxHeight: '400px', overflowY: 'auto' }}>
                                            {(() => {
                                                const totalBillableWeight = packages.reduce((sum, pkg) => sum + getBillableWeight(pkg), 0);
                                                return accounts.map(acc => {
                                                    const accountRatesData = accountRates[acc];
                                                    const isLoading = loadingAccounts[acc];
                                                    const accountError = accountErrors[acc];

                                                    // Validate GL range for this account
                                                    const matchedRange = companySlug && companySettings.glAccountRanges?.find(r =>
                                                        totalBillableWeight >= parseFloat(r.minWeight) &&
                                                        totalBillableWeight <= parseFloat(r.maxWeight) &&
                                                        (!r.upsAccount || r.upsAccount.trim().toUpperCase() === acc.trim().toUpperCase())
                                                    );
                                                    const isLocked = companySlug && companySettings.glAccountRanges?.length > 0 && totalBillableWeight > 0 && !matchedRange;

                                                    return (
                                                        <div
                                                            key={acc}
                                                            className="account-card-small"
                                                            style={{
                                                                background: isLoading ? '#f0f4f8' : (isLocked || accountError ? '#fef5f5' : (accountRatesData ? 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)' : 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)')),
                                                                border: (isLocked || accountError) ? '2px solid #fc8181' : (selectedAccount === acc && accountRatesData ? '2px solid #3182ce' : '2px solid #e2e8f0'),
                                                                borderRadius: '10px',
                                                                padding: '1rem',
                                                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                                                transition: 'all 0.3s ease',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '0.5rem',
                                                                minHeight: '120px',
                                                                justifyContent: 'space-between',
                                                                opacity: isLocked ? 0.8 : 1
                                                            }}
                                                            onClick={() => {
                                                                if (isLocked) return;
                                                                if (accountRatesData) {
                                                                    setExpandedAccountId(acc);
                                                                    setSelectedAccount(acc);
                                                                }
                                                            }}
                                                        >
                                                            {isLoading ? (
                                                                <>
                                                                    <div style={{ fontSize: '0.6rem', color: '#718096', fontWeight: '600' }}>{acc}</div>
                                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flex: 1 }}>
                                                                        <div style={{ width: '1rem', height: '1rem', border: '2px solid #e2e8f0', borderTop: '2px solid #3182ce', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                                                        <span style={{ fontSize: '0.7rem', color: '#4299e1' }}>Loading...</span>
                                                                    </div>
                                                                </>
                                                            ) : isLocked ? (
                                                                <>
                                                                    <div style={{ fontSize: '0.7rem', color: '#c53030', fontWeight: '600' }}>⚠️ {acc}</div>
                                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <div style={{ fontSize: '0.65rem', color: '#742a2a', textAlign: 'center', fontWeight: '600', lineHeight: '1.3' }}>
                                                                            GL number is missing for this weight range, use other account number
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            ) : accountError ? (
                                                                <>
                                                                    <div style={{ fontSize: '0.7rem', color: '#c53030', fontWeight: '600' }}>❌ {acc}</div>
                                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <div style={{ fontSize: '0.65rem', color: '#742a2a', textAlign: 'center', fontWeight: '500', lineHeight: '1.3' }}>
                                                                            {accountError}
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            ) : accountRatesData ? (
                                                                <>
                                                                    <div style={{ fontSize: '0.7rem', color: '#4a5568', fontWeight: '600' }}>
                                                                        {acc}
                                                                        {accountComments[acc] && (
                                                                            <span style={{ marginLeft: '4px', background: '#fefcbf', color: '#b7791f', padding: '1px 4px', borderRadius: '4px', fontSize: '0.6rem' }}>
                                                                                {accountComments[acc]}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.25rem' }}>
                                                                        <div style={{ fontSize: '0.6rem', color: '#718096' }}>Grand Total</div>
                                                                        <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#2b6cb0' }}>PKR</div>
                                                                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#2b6cb0', wordBreak: 'break-all' }}>{accountRatesData.breakdown.grandTotalWithDDPPKR || accountRatesData.breakdown.grandTotalPKR}</div>
                                                                    </div>
                                                                    <div style={{ fontSize: '0.65rem', color: '#4299e1', fontStyle: 'italic', textAlign: 'center' }}>
                                                                        {accountRatesData.breakdown.ddp ? '🚀 DDP • Click to expand' : 'Click to expand'}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div style={{ fontSize: '0.7rem', color: '#4a5568', fontWeight: '600' }}>{acc}</div>
                                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <div style={{ fontSize: '0.7rem', color: '#718096', textAlign: 'center', fontStyle: 'italic' }}>
                                                                            Click "Get All Quotes" to fetch rates
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // Expanded Account View
                                <>
                                    <button
                                        onClick={() => {
                                            setExpandedAccountId(null);
                                            setSelectedAccount('');
                                        }}
                                        style={{
                                            background: '#edf2f7',
                                            border: '1px solid #cbd5e0',
                                            padding: '0.5rem 1rem',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            color: '#2d3748',
                                            fontWeight: '600',
                                            marginBottom: '1rem',
                                            width: '100%',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = '#e2e8f0'}
                                        onMouseLeave={(e) => e.target.style.background = '#edf2f7'}
                                    >
                                        ← Back to Accounts
                                    </button>

                                    <div className="summary-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <div style={{ fontSize: '0.9rem', color: '#718096', fontWeight: 'normal' }}>Account & Billing</div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#2b6cb0' }}>{expandedAccountId}</div>
                                                {ddpTransportationPayer === 'BillThirdParty' && (
                                                    <span style={{ fontSize: '0.65rem', padding: '2px 6px', background: '#e2e8f0', color: '#2b6cb0', borderRadius: '4px', fontWeight: 'bold' }}>3RD PARTY</span>
                                                )}
                                                {ddpDutyTaxPayer === 'BillReceiver' && (
                                                    <span style={{ fontSize: '0.65rem', padding: '2px 6px', background: '#f0fff4', color: '#38a169', borderRadius: '4px', fontWeight: 'bold' }}>DUTY TO REC.</span>
                                                )}
                                                {isDDP && (
                                                    <span style={{ fontSize: '0.65rem', padding: '2px 6px', background: '#fff5f5', color: '#c53030', borderRadius: '4px', fontWeight: 'bold' }}>DDP</span>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '0.9rem', color: '#718096', fontWeight: 'normal', marginBottom: '0.25rem' }}>Total Charges</div>
                                            <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#059669' }}>PKR {accountRates[expandedAccountId]?.breakdown?.grandTotalWithDDPPKR || accountRates[expandedAccountId]?.breakdown?.grandTotalPKR}</div>
                                        </div>
                                    </div>

                                    {loadingAccounts[expandedAccountId] ? (
                                        <div style={{ textAlign: 'center', color: '#a0aec0', padding: '2rem 0' }}>
                                            <div style={{ fontSize: '0.9rem' }}>Calculating rates...</div>
                                        </div>
                                    ) : accountRates[expandedAccountId] ? (
                                        <>
                                            {accountRates[expandedAccountId].breakdown ? (
                                                <>
                                                    <div style={{ marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                                                        <div className="cost-row">
                                                            <span style={{ fontWeight: 'bold', color: '#4a5568' }}>Billing Weight</span>
                                                            <span style={{ fontWeight: 'bold' }}>{accountRates[expandedAccountId].breakdown.totalWeight} KGS</span>
                                                        </div>
                                                        <div className="cost-row total" style={{ color: '#2b6cb0', marginTop: '0.5rem', fontSize: '1.2rem' }}>
                                                            <span>Total (PKR)</span>
                                                            <span>{accountRates[expandedAccountId].breakdown.grandTotalPKR}</span>
                                                        </div>
                                                    </div>

                                                    <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                                                        <thead>
                                                            <tr style={{ borderBottom: '1px solid #cbd5e0', color: '#718096' }}>
                                                                <th style={{ textAlign: 'left', padding: '4px 0' }}>Item</th>
                                                                <th style={{ textAlign: 'right', padding: '4px 0' }}>USD</th>
                                                                <th style={{ textAlign: 'right', padding: '4px 0' }}>PKR</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td style={{ padding: '4px 0' }}>Freight</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.freightUSD}</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.freightPKR}</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0' }}>SBC (Fixed)</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.sbcUSD}</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.sbcPKR}</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0' }}>IPF (Code 573)</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ipfUSD}</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ipfPKR}</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0' }}>Insurance</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.insuranceUSD}</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.insurancePKR}</td>
                                                            </tr>

                                                            {(parseFloat(accountRates[expandedAccountId].breakdown.deliverySurchargeUSD) > 0) && (
                                                                <tr>
                                                                    <td style={{ padding: '4px 0' }}>Delivery Surcharge</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.deliverySurchargeUSD}</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.deliverySurchargePKR}</td>
                                                                </tr>
                                                            )}

                                                            {(parseFloat(accountRates[expandedAccountId].breakdown.largePkgSurchargeUSD) > 0) && (
                                                                <tr>
                                                                    <td style={{ padding: '4px 0' }}>Large Pkg Surcharge</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.largePkgSurchargeUSD}</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.largePkgSurchargePKR}</td>
                                                                </tr>
                                                            )}

                                                            {(parseFloat(accountRates[expandedAccountId].breakdown.additionalHandlingUSD) > 0) && (
                                                                <tr>
                                                                    <td style={{ padding: '4px 0' }}>Add. Handling</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.additionalHandlingUSD}</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.additionalHandlingPKR}</td>
                                                                </tr>
                                                            )}

                                                            {(parseFloat(accountRates[expandedAccountId].breakdown.peakSeasonLargePackageUSD || 0) > 0) && (
                                                                <tr>
                                                                    <td style={{ padding: '4px 0' }}>Peak Season Surcharge - LP (UPS Code 431)</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.peakSeasonLargePackageUSD}</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.peakSeasonLargePackagePKR}</td>
                                                                </tr>
                                                            )}

                                                            {(parseFloat(accountRates[expandedAccountId].breakdown.peakSeasonAdditionalHandlingUSD || 0) > 0) && (
                                                                <tr>
                                                                    <td style={{ padding: '4px 0' }}>Peak Season Surcharge - AH (UPS Code 432)</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.peakSeasonAdditionalHandlingUSD}</td>
                                                                    <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.peakSeasonAdditionalHandlingPKR}</td>
                                                                </tr>
                                                            )}

                                                            <tr>
                                                                <td style={{ padding: '4px 0' }}>Fuel ({accountRates[expandedAccountId].breakdown.fuelUsed})</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.fuelSurchargeUSD}</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.fuelSurchargePKR}</td>
                                                            </tr>

                                                            {accountRates[expandedAccountId]?.breakdown?.peakChargesUSD && parseFloat(accountRates[expandedAccountId].breakdown.peakChargesUSD) > 0 && (
                                                                <tr style={{ backgroundColor: '#fffaf0' }}>
                                                                    <td style={{ padding: '4px 0', fontWeight: 'bold', color: '#d97706' }}>Peak Period Charges</td>
                                                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#d97706' }}>{accountRates[expandedAccountId].breakdown.peakChargesUSD}</td>
                                                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#d97706' }}>{accountRates[expandedAccountId].breakdown.peakChargesPKR}</td>
                                                                </tr>
                                                            )}

                                                            {accountRates[expandedAccountId]?.breakdown?.specialSurchargeUSD && parseFloat(accountRates[expandedAccountId].breakdown.specialSurchargeUSD) > 0 && (
                                                                <tr style={{ backgroundColor: '#eef2ff' }}>
                                                                    <td style={{ padding: '4px 0', fontWeight: 'bold', color: '#4f46e5' }}>🌍 Country Surcharge ({accountRates[expandedAccountId].breakdown.specialSurchargeCountry})</td>
                                                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#4f46e5' }}>{accountRates[expandedAccountId].breakdown.specialSurchargeUSD}</td>
                                                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#4f46e5' }}>{accountRates[expandedAccountId].breakdown.specialSurchargePKR}</td>
                                                                </tr>
                                                            )}

                                                            <tr style={{ borderTop: '1px solid #e2e8f0', fontWeight: 'bold' }}>
                                                                <td style={{ padding: '6px 0' }}>TOTAL BEFORE TAX</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.subtotalUSD}</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.subtotalPKR}</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0' }}>{accountRates[expandedAccountId].breakdown.taxPercentage || 'Sales Tax (GST 15%)'}</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.salesTaxUSD}</td>
                                                                <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.salesTaxPKR}</td>
                                                            </tr>
                                                            <tr style={{ borderTop: '2px solid #2d3748', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                                <td style={{ padding: '8px 0' }}>TOTAL WITH TAX</td>
                                                                <td colSpan="2" style={{ textAlign: 'right' }}>PKR {accountRates[expandedAccountId].breakdown.totalPKR}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>

                                                    {/* DDP Charges Section */}
                                                    {accountRates[expandedAccountId].breakdown.ddpError && (
                                                        <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fed7d7', border: '1px solid #e53e3e', borderRadius: '4px' }}>
                                                            <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#c53030' }}>🚨 DDP Calculation Error</div>
                                                            <div style={{ fontSize: '0.8rem', color: '#742a2a', marginTop: '0.5rem' }}>{accountRates[expandedAccountId].breakdown.ddpError}</div>
                                                        </div>
                                                    )}
                                                    {accountRates[expandedAccountId].breakdown.ddp && (
                                                        <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px solid #ed8936' }}>
                                                            <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#c53030', marginBottom: '0.75rem' }}>🚀 DDP Charges</div>
                                                            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                                                                <tbody>
                                                                    {(parseFloat(accountRates[expandedAccountId].breakdown.ddp.totalDuties) > 0) && (
                                                                        <tr>
                                                                            <td style={{ padding: '4px 0' }}>Total Duties</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalDuties}</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalDutiesPKR}</td>
                                                                        </tr>
                                                                    )}
                                                                    {(parseFloat(accountRates[expandedAccountId].breakdown.ddp.totalVAT) > 0) && (
                                                                        <tr>
                                                                            <td style={{ padding: '4px 0' }}>Total VAT</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalVAT}</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalVATPKR}</td>
                                                                        </tr>
                                                                    )}
                                                                    {(parseFloat(accountRates[expandedAccountId].breakdown.ddp.totalCommodityLevelTaxesAndFees) > 0) && (
                                                                        <tr>
                                                                            <td style={{ padding: '4px 0' }}>Commodity Level Taxes/Fees</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalCommodityLevelTaxesAndFees}</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalCommodityLevelTaxesAndFeesPKR}</td>
                                                                        </tr>
                                                                    )}
                                                                    {(parseFloat(accountRates[expandedAccountId].breakdown.ddp.totalShipmentLevelTaxesAndFees) > 0) && (
                                                                        <tr>
                                                                            <td style={{ padding: '4px 0' }}>Shipment Level Taxes/Fees</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalShipmentLevelTaxesAndFees}</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalShipmentLevelTaxesAndFeesPKR}</td>
                                                                        </tr>
                                                                    )}
                                                                    {(parseFloat(accountRates[expandedAccountId].breakdown.ddp.brokerageFees) > 0) && (
                                                                        <tr>
                                                                            <td style={{ padding: '4px 0' }}>Brokerage Fees</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.brokerageFees}</td>
                                                                            <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.brokerageFeesPKR}</td>
                                                                        </tr>
                                                                    )}
                                                                    <tr>
                                                                        <td style={{ padding: '4px 0' }}>F/D Charges Inc. GST</td>
                                                                        <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.fdCharges}</td>
                                                                        <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.fdChargesPKR}</td>
                                                                    </tr>
                                                                    <tr style={{ borderTop: '1px solid #fed7d7', fontWeight: 'bold', color: '#c53030' }}>
                                                                        <td style={{ padding: '6px 0' }}>Total DDP Cost</td>
                                                                        <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalDDPCost}</td>
                                                                        <td style={{ textAlign: 'right' }}>{accountRates[expandedAccountId].breakdown.ddp.totalDDPCostPKR}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}

                                                    {/* Grand Total with DDP */}
                                                    {accountRates[expandedAccountId].breakdown.ddp && (
                                                        <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef5e7', border: '2px solid #f39c12', borderRadius: '4px' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '0.95rem', color: '#c53030' }}>
                                                                <span>GRAND TOTAL (Shipping + DDP)</span>
                                                                <span>{accountRates[expandedAccountId].breakdown.grandTotalWithDDP} USD</span>
                                                            </div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '0.95rem', color: '#c53030', marginTop: '0.5rem' }}>
                                                                <span>GRAND TOTAL PKR</span>
                                                                <span>{accountRates[expandedAccountId].breakdown.grandTotalWithDDPPKR} PKR</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div style={{ fontSize: '0.7rem', color: '#718096', fontStyle: 'italic', marginBottom: '1rem', marginTop: '1rem' }}>
                                                        * Base Currency: USD. Conversion Rate: {accountRates[expandedAccountId].breakdown.rateUsed} PKR. Fuel Rate: {accountRates[expandedAccountId].breakdown.fuelUsed}
                                                    </div>

                                                    {/* Payment and Shipment Buttons - Inside expanded account view */}
                                                    {isPaymentReceived ? (
                                                        <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: '8px', color: '#2f855a', textAlign: 'center' }}>
                                                            <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>✅ Payment Received</div>
                                                            <div style={{ fontSize: '0.85rem' }}>{paymentMethod}: {collectAmount} PKR</div>
                                                            {transactionId && <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>ID: {transactionId}</div>}
                                                            <button
                                                                onClick={() => setShowPaymentModal(true)}
                                                                style={{ marginTop: '0.5rem', background: 'transparent', border: 'none', color: '#2b6cb0', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.8rem' }}
                                                            >
                                                                Edit Payment
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            className="action-btn secondary"
                                                            onClick={() => {
                                                                let amountPKR = accountRates[expandedAccountId].breakdown?.grandTotalWithDDPPKR || accountRates[expandedAccountId].breakdown?.grandTotalPKR || accountRates[expandedAccountId].breakdown?.totalPKR;
                                                                setCollectAmount(amountPKR);
                                                                setShowPaymentModal(true);
                                                            }}
                                                            disabled={loading}
                                                            style={{ marginTop: '1.5rem', width: '100%', padding: '0.75rem', fontSize: '1rem', background: '#ebf8ff', color: '#2b6cb0', borderColor: '#bee3f8' }}
                                                        >
                                                            💰 Receive Payment
                                                        </button>
                                                    )}



                                                    <button
                                                        className="action-btn primary"
                                                        onClick={handleCreateShipment}
                                                        disabled={loading || !isPaymentReceived}
                                                        style={{
                                                            marginTop: '0.75rem',
                                                            width: '100%',
                                                            padding: '0.75rem 1rem',
                                                            fontSize: '1rem',
                                                            fontWeight: 'bold',
                                                            background: isPaymentReceived ? '#2b6cb0' : '#cbd5e0',
                                                            opacity: (loading || !isPaymentReceived) ? 0.7 : 1,
                                                            cursor: (loading || !isPaymentReceived) ? 'default' : 'pointer'
                                                        }}
                                                    >
                                                        {loading ? '⏳ Processing...' : (!isPaymentReceived ? '🔒 Receive Payment First' : '📦 Create Shipment')}
                                                    </button>
                                                </>
                                            ) : null}
                                        </>
                                    ) : null}
                                </>
                            )}

                            {/* Original Rates Section - Hidden when using cards */}
                            {expandedAccountId === null && !accountRates[selectedAccount] && (
                                <div style={{ textAlign: 'center', color: '#a0aec0', padding: '2rem 0' }}>
                                    {Object.keys(accountRates).length > 0
                                        ? 'Select a card to view detailed rates'
                                        : (loading ? 'Fetching quotes...' : 'Click "Get All Quotes" to view available rates')}
                                </div>
                            )}

                            {!rates && (
                                <button
                                    className="action-btn secondary"
                                    onClick={() => handleGetRates()}
                                    disabled={loading}
                                >
                                    {loading ? 'Processing...' : 'Get All Quotes'}
                                </button>
                            )}

                            <div style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: '#718096', fontStyle: 'italic' }}>
                                * Rates are estimates. DDP charges are calculated based on provided HS Codes and values using UPS Landed Cost API.
                                Final billing may vary.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {
                view === 'list' && (
                    <>
                        {/* History Section */}
                        <div className="shipping-history" style={{ marginTop: '3rem', background: 'white', borderRadius: '20px', padding: '2rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                            {/* Backend-filtered pagination */}
                            {(() => {
                                const hasMore = history.length < totalHistoryCount;

                                return (
                                    <>
                                        <div className="section-title" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <span>Recent Shipments</span>
                                                <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '4px' }}>
                                                    Showing {history.length} of {totalHistoryCount} shipment{totalHistoryCount !== 1 ? 's' : ''}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                {/* Search Box */}
                                                <input
                                                    type="text"
                                                    placeholder="🔍 Tracking # or Email…"
                                                    value={historySearch}
                                                    onChange={handleSearch}
                                                    style={{
                                                        fontSize: '0.84rem', padding: '6px 12px', borderRadius: '999px',
                                                        border: '1px solid #cbd5e0', background: '#f7fafc',
                                                        outline: 'none', minWidth: 200, color: '#2d3748'
                                                    }}
                                                />
                                                <button
                                                    onClick={() => {
                                                        const filters = {
                                                            dateFrom: filterDateFrom,
                                                            dateTo: filterDateTo,
                                                            tracking: filterTracking,
                                                            recipient: filterRecipient,
                                                            createdBy: filterCreatedBy,
                                                            costMin: filterCostMin,
                                                            costMax: filterCostMax,
                                                            status: filterStatus
                                                        };
                                                        fetchHistory(1, historySearch, false, filters);
                                                    }}
                                                    style={{ fontSize: '0.85rem', padding: '6px 12px', borderRadius: '999px', border: '1px solid #cbd5e0', background: '#f7fafc', cursor: 'pointer' }}
                                                >
                                                    Refresh
                                                </button>
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    <button
                                                        onClick={handleBulkViewLabels}
                                                        disabled={selectedShipments.length === 0}
                                                        title="View label(s) for selected shipment(s)"
                                                        style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #cbd5e0', background: '#ebf8ff', color: '#2b6cb0', cursor: selectedShipments.length === 0 ? 'default' : 'pointer', opacity: selectedShipments.length === 0 ? 0.6 : 1 }}
                                                    >
                                                        Label
                                                    </button>
                                                    <button
                                                        onClick={handleBulkViewBilling}
                                                        disabled={selectedShipments.length !== 1}
                                                        title="View billing for selected shipment"
                                                        style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #cbd5e0', background: '#f3e8ff', color: '#6b46c1', cursor: selectedShipments.length !== 1 ? 'default' : 'pointer', opacity: selectedShipments.length !== 1 ? 0.6 : 1 }}
                                                    >
                                                        Billing
                                                    </button>
                                                    {!companySlug && (
                                                        <button
                                                            onClick={async () => {
                                                                if (selectedShipments.length !== 1) return;
                                                                const tracking = selectedShipments[0];
                                                                let item = history.find(h => h.trackingNumber === tracking);
                                                                if (!item) return;

                                                                const amountPKR = item.detailedBreakdown
                                                                    ? (item.detailedBreakdown.grandTotalWithDDPPKR ||
                                                                        item.detailedBreakdown.grandTotalPKR ||
                                                                        item.detailedBreakdown.totalPKR)
                                                                    : null;

                                                                // For voided shipments, allow showing receipt even without amount
                                                                const displayAmount = amountPKR || 0;
                                                                if (!displayAmount && item.status !== 'voided') {
                                                                    alert('Total amount in PKR not found for this shipment.');
                                                                    return;
                                                                }

                                                                // Use saved receipt number - invoiceNo is the receipt number in the database
                                                                // commodityInvoiceNo is for future commercial invoices
                                                                const receiptNumber = item.invoiceNo || item.commodityInvoiceNo;

                                                                if (!receiptNumber) {
                                                                    alert('Receipt/Invoice number not found for this shipment record. Please ensure the shipment was created with a receipt number.');
                                                                    return;
                                                                }

                                                                openReceiptWindow({
                                                                    receiptNo: receiptNumber,
                                                                    trackingNumber: item.trackingNumber,
                                                                    amountPKR: displayAmount,
                                                                    receiverName: item.shipper?.name || item.origin?.name || 'Universal Logistics Services (Pvt.) Ltd.',
                                                                    createdBy: item.createdBy || item.user || currentUser || 'system',
                                                                    receiptEnabled: item.receiptEnabled,
                                                                    packages: item.packages || [],
                                                                    countryOfOrigin: item.destination?.country ? getCountryName(item.destination.country) : (item.receiptDestinationCountry ? getCountryName(item.receiptDestinationCountry) : 'N/A'),
                                                                    totalWeight: item.billingWeight || item.receiptTotalWeight || (item.packages ? item.packages.reduce((sum, pkg) => sum + getBillableWeight(pkg), 0) : 0),
                                                                    paymentMethod: item.paymentMethod || '',
                                                                    transactionId: item.transactionId || ''
                                                                });
                                                            }}
                                                            disabled={selectedShipments.length !== 1}
                                                            title="View receipt for selected shipment"
                                                            style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #cbd5e0', background: '#fffaf0', color: '#975a16', cursor: selectedShipments.length !== 1 ? 'default' : 'pointer', opacity: selectedShipments.length !== 1 ? 0.6 : 1 }}
                                                        >
                                                            Receipt
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={handleBulkCommercialInvoice}
                                                        disabled={selectedShipments.length !== 1}
                                                        title="Open commercial invoice for selected shipment"
                                                        style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #cbd5e0', background: '#fffef0', color: '#805ad5', cursor: selectedShipments.length !== 1 ? 'default' : 'pointer', opacity: selectedShipments.length !== 1 ? 0.6 : 1 }}
                                                    >
                                                        Commercial Invoice
                                                    </button>
                                                    <button
                                                        onClick={handleBulkDuplicate}
                                                        disabled={selectedShipments.length !== 1}
                                                        title="Duplicate selected shipment into form"
                                                        style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #cbd5e0', background: '#e6fffa', color: '#2c7a7b', cursor: selectedShipments.length !== 1 ? 'default' : 'pointer', opacity: selectedShipments.length !== 1 ? 0.6 : 1 }}
                                                    >
                                                        Duplicate
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const selectedItem = history.find(h => selectedShipments.includes(h.trackingNumber));
                                                            if (selectedItem && selectedItem.status === 'draft') {
                                                                handleBulkProcessDraft();
                                                            }
                                                        }}
                                                        disabled={selectedShipments.length !== 1 || !history.some(h => selectedShipments.includes(h.trackingNumber) && h.status === 'draft')}
                                                        title="Load draft shipment data into form for completion"
                                                        style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #86efac', background: '#f0fdf4', color: '#166534', cursor: (selectedShipments.length !== 1 || !history.some(h => selectedShipments.includes(h.trackingNumber) && h.status === 'draft')) ? 'default' : 'pointer', opacity: (selectedShipments.length !== 1 || !history.some(h => selectedShipments.includes(h.trackingNumber) && h.status === 'draft')) ? 0.6 : 1 }}
                                                    >
                                                        Process Shipment
                                                    </button>
                                                    <button
                                                        onClick={handleBulkVoidShipments}
                                                        disabled={!history.some(h => selectedShipments.includes(h.trackingNumber) && h.status !== 'voided')}
                                                        title="Void selected active shipment(s)"
                                                        style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #fed7d7', background: '#fff5f5', color: '#c53030', cursor: !history.some(h => selectedShipments.includes(h.trackingNumber) && h.status !== 'voided') ? 'default' : 'pointer', opacity: !history.some(h => selectedShipments.includes(h.trackingNumber) && h.status !== 'voided') ? 0.6 : 1 }}
                                                    >
                                                        Void
                                                    </button>
                                                    {isSuperUser && (
                                                        <button
                                                            onClick={handleBulkDeleteShipments}
                                                            disabled={selectedShipments.length === 0 || !history.some(h => selectedShipments.includes(h.trackingNumber) && h.status === 'voided')}
                                                            title="Delete selected voided shipment(s) from history"
                                                            style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #feb2b2', background: '#e53e3e', color: 'white', cursor: (selectedShipments.length === 0 || !history.some(h => selectedShipments.includes(h.trackingNumber) && h.status === 'voided')) ? 'default' : 'pointer', opacity: (selectedShipments.length === 0 || !history.some(h => selectedShipments.includes(h.trackingNumber) && h.status === 'voided')) ? 0.6 : 1 }}
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={handleDownloadAllFiltered}
                                                        title="Download all matching current filters as CSV"
                                                        style={{ fontSize: '0.8rem', padding: '6px 12px', borderRadius: '999px', border: '1px solid #c3dafe', background: '#ebf4ff', color: '#2b6cb0', cursor: 'pointer', fontWeight: 600 }}
                                                    >
                                                        📁 Download All Filtered
                                                    </button>
                                                    <button
                                                        onClick={handleDownloadShipments}
                                                        disabled={selectedShipments.length === 0}
                                                        title="Download selected shipment(s) as CSV"
                                                        style={{ fontSize: '0.8rem', padding: '6px 10px', borderRadius: '999px', border: '1px solid #bee3f8', background: '#3182ce', color: 'white', cursor: selectedShipments.length === 0 ? 'default' : 'pointer', opacity: selectedShipments.length === 0 ? 0.6 : 1 }}
                                                    >
                                                        ⬇️ Download CSV
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Filters Section */}
                                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', padding: '1rem', background: '#f8fafc', borderRadius: '12px' }}>
                                            <input
                                                type="date"
                                                value={filterDateFrom}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFilterDateFrom(val);
                                                }}
                                                placeholder="Date From"
                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', minWidth: '140px' }}
                                            />
                                            <input
                                                type="date"
                                                value={filterDateTo}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFilterDateTo(val);
                                                }}
                                                placeholder="Date To"
                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', minWidth: '140px' }}
                                            />
                                            <input
                                                type="text"
                                                value={filterTracking}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFilterTracking(val);
                                                }}
                                                placeholder="🔍 Tracking #"
                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', minWidth: '140px' }}
                                            />
                                            <input
                                                type="text"
                                                value={filterRecipient}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFilterRecipient(val);
                                                }}
                                                placeholder="👤 Recipient"
                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', minWidth: '140px' }}
                                            />
                                            <input
                                                type="text"
                                                value={filterCreatedBy}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFilterCreatedBy(val);
                                                }}
                                                placeholder="✍️ Created By"
                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', minWidth: '140px' }}
                                            />
                                            <input
                                                type="number"
                                                value={filterCostMin}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFilterCostMin(val);
                                                }}
                                                placeholder="Min Cost (PKR)"
                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', minWidth: '140px' }}
                                            />
                                            <input
                                                type="number"
                                                value={filterCostMax}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFilterCostMax(val);
                                                }}
                                                placeholder="Max Cost (PKR)"
                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', minWidth: '140px' }}
                                            />
                                            <select
                                                value={filterStatus}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFilterStatus(val);
                                                }}
                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', minWidth: '120px' }}
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="active">Active</option>
                                                <option value="draft">Draft</option>
                                                <option value="voided">Voided</option>
                                            </select>
                                            <button
                                                onClick={() => {
                                                    const filters = { dateFrom: filterDateFrom, dateTo: filterDateTo, tracking: filterTracking, recipient: filterRecipient, createdBy: filterCreatedBy, costMin: filterCostMin, costMax: filterCostMax, status: filterStatus };
                                                    fetchHistory(1, historySearch, false, filters);
                                                }}
                                                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e0', background: '#3182ce', color: 'white', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}
                                            >
                                                Apply Filters
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setFilterDateFrom('');
                                                    setFilterDateTo('');
                                                    setFilterTracking('');
                                                    setFilterRecipient('');
                                                    setFilterCreatedBy('');
                                                    setFilterCostMin('');
                                                    setFilterCostMax('');
                                                    setFilterStatus('');
                                                    fetchHistory(1, historySearch, false, {});
                                                }}
                                                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e0', background: '#e2e8f0', color: '#2d3748', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}
                                            >
                                                Clear Filters
                                            </button>
                                        </div>

                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid #edf2f7', color: '#4a5568' }}>
                                                        <th style={{ padding: '1rem', textAlign: 'center', width: '40px' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={history.length > 0 && history.every(h => selectedShipments.includes(h.trackingNumber))}
                                                                onChange={() => toggleSelectAllShipments(history)}
                                                            />
                                                        </th>
                                                        <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
                                                        <th style={{ padding: '1rem', textAlign: 'left' }}>Tracking</th>
                                                        <th style={{ padding: '1rem', textAlign: 'left' }}>Recipient</th>
                                                        <th style={{ padding: '1rem', textAlign: 'left' }}>Created By</th>
                                                        <th style={{ padding: '1rem', textAlign: 'right' }}>Total Cost (PKR)</th>
                                                        <th style={{ padding: '1rem', textAlign: 'center' }}>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody style={{ position: 'relative', minHeight: '200px' }}>
                                                    {isHistoryLoading && !history.length && (
                                                        <tr>
                                                            <td colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>
                                                                <div className="spinner" style={{ margin: '0 auto 10px' }}></div>
                                                                <p style={{ color: '#718096', fontSize: '0.85rem' }}>Searching records...</p>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {isHistoryLoading && history.length > 0 && (
                                                        <tr style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.5)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <td colSpan="7" style={{ border: 'none' }}></td>
                                                        </tr>
                                                    )}
                                                    {history.length === 0 && !isHistoryLoading ? (
                                                        <tr><td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: '#a0aec0' }}>
                                                            {historySearch ? `No shipments match "${historySearch}"` : 'No history found'}
                                                        </td></tr>
                                                    ) : (
                                                        !isHistoryLoading && history.length === 0 ? null : history.map(item => (
                                                            <tr key={item.trackingNumber} style={{ borderBottom: '1px solid #edf2f7', transition: 'background 0.2s' }}>
                                                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedShipments.includes(item.trackingNumber)}
                                                                        onChange={() => toggleSelectShipment(item.trackingNumber)}
                                                                    />
                                                                </td>
                                                                <td style={{ padding: '1rem' }}>{new Date(item.shipmentDate).toLocaleDateString()} {new Date(item.shipmentDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                                                <td style={{ padding: '1rem', fontFamily: 'monospace', fontWeight: 'bold', color: '#2b6cb0' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        {item.trackingNumber}
                                                                        <button
                                                                            onClick={() => {
                                                                                console.log('[Shipping] Opening details for:', {
                                                                                    trackingNumber: item.trackingNumber,
                                                                                    isDDP: item.isDDP,
                                                                                    status: item.status
                                                                                });
                                                                                setShowDetails(item);
                                                                            }}
                                                                            style={{
                                                                                padding: '2px',
                                                                                background: 'transparent',
                                                                                border: 'none',
                                                                                cursor: 'pointer',
                                                                                fontSize: '1.1rem',
                                                                                color: '#4a5568',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                transition: 'color 0.2s'
                                                                            }}
                                                                            onMouseOver={(e) => e.currentTarget.style.color = '#3182ce'}
                                                                            onMouseOut={(e) => e.currentTarget.style.color = '#4a5568'}
                                                                            title="View Details"
                                                                        >
                                                                            👁️
                                                                        </button>
                                                                        {isSuperUser && item.status === 'voided' && (
                                                                            <button
                                                                                onClick={() => handleDeleteShipment(item.trackingNumber)}
                                                                                style={{
                                                                                    padding: '2px',
                                                                                    background: 'transparent',
                                                                                    border: 'none',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '1.1rem',
                                                                                    color: '#e53e3e',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    transition: 'color 0.2s'
                                                                                }}
                                                                                onMouseOver={(e) => e.currentTarget.style.color = '#c53030'}
                                                                                onMouseOut={(e) => e.currentTarget.style.color = '#e53e3e'}
                                                                                title="Delete from history"
                                                                            >
                                                                                🗑️
                                                                            </button>
                                                                        )}
                                                                        {item.status === 'draft' && (
                                                                            <button
                                                                                onClick={() => handleDeleteDraft(item.trackingNumber)}
                                                                                style={{
                                                                                    padding: '2px',
                                                                                    background: 'transparent',
                                                                                    border: 'none',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '1.1rem',
                                                                                    color: '#e53e3e',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    transition: 'color 0.2s'
                                                                                }}
                                                                                onMouseOver={(e) => e.currentTarget.style.color = '#c53030'}
                                                                                onMouseOut={(e) => e.currentTarget.style.color = '#e53e3e'}
                                                                                title="Delete Draft"
                                                                            >
                                                                                🗑️
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td style={{ padding: '1rem' }}>{item.destination?.name || item.destination?.company || item.destination?.attention || 'N/A'}</td>
                                                                <td style={{ padding: '1rem', color: '#4a5568', fontSize: '0.85rem' }}>{item.createdBy || item.user || currentUser || 'Unknown'}</td>
                                                                <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold' }}>
                                                                    {item.detailedBreakdown && (item.detailedBreakdown.grandTotalWithDDPPKR || item.detailedBreakdown.grandTotalPKR)
                                                                        ? (item.detailedBreakdown.grandTotalWithDDPPKR || item.detailedBreakdown.grandTotalPKR)
                                                                        : (item.detailedBreakdown && item.detailedBreakdown.totalPKR
                                                                            ? item.detailedBreakdown.totalPKR
                                                                            : `${item.currency || 'USD'} ${(item.totalCharges || '0').toString()}`)}
                                                                </td>
                                                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                                    <span style={{
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        background: item.status === 'voided' ? '#fed7d7' : item.status === 'draft' ? '#fef3c7' : '#c6f6d5',
                                                                        color: item.status === 'voided' ? '#9b2c2c' : item.status === 'draft' ? '#92400e' : '#2f855a',
                                                                        fontSize: '0.8rem', fontWeight: 'bold'
                                                                    }}>
                                                                        {item.status ? item.status.toUpperCase() : 'ACTIVE'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* Load More */}
                                        {hasMore && (
                                            <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                                                <button
                                                    onClick={handleLoadMore}
                                                    disabled={isLoadingMore}
                                                    style={{
                                                        padding: '8px 28px', borderRadius: '999px', border: '1px solid #cbd5e0',
                                                        background: isLoadingMore ? '#cbd5e0' : '#edf2f7', color: '#4a5568', fontSize: '0.875rem',
                                                        cursor: isLoadingMore ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: isLoadingMore ? 0.6 : 1
                                                    }}
                                                >
                                                    {isLoadingMore ? 'Loading...' : `Load More (${totalHistoryCount - history.length} remaining)`}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        {/* Label Modal */}
                        {viewLabel && Array.isArray(viewLabel) && viewLabel.length > 0 && (
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={() => setViewLabel(null)}>
                                <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', maxWidth: '90%', maxHeight: '90vh', overflow: 'auto', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                    <h3 style={{ marginBottom: '1rem' }}>Shipping Label(s)</h3>
                                    {viewLabel.map((lbl, idx) => {
                                        const isPDF = lbl.startsWith('JVBERi') || lbl.startsWith('JVB') || (lbl.substring(0, 10).indexOf('JVBER') !== -1);
                                        return isPDF ? (
                                            <iframe
                                                key={idx}
                                                src={`data:application/pdf;base64,${lbl}`}
                                                title={`Document ${idx + 1}`}
                                                style={{ width: '100%', height: '800px', border: '1px solid #ccc', marginBottom: idx < viewLabel.length - 1 ? '1rem' : 0 }}
                                            />
                                        ) : (
                                            <img
                                                key={idx}
                                                src={`data:image/gif;base64,${lbl}`}
                                                alt={`Shipping Label ${idx + 1}`}
                                                style={{ maxWidth: '100%', border: '1px solid #ccc', marginBottom: idx < viewLabel.length - 1 ? '1rem' : 0 }}
                                            />
                                        );
                                    })}
                                    <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                                        <button onClick={() => {
                                            const win = window.open('');
                                            const labels = viewLabel;
                                            const html = `<!DOCTYPE html>
<html>
<head>
  <title>Shipping Labels</title>
  <style>
    @page { size: A4; margin: 10mm; }
    body { margin: 0; padding: 0; }
    .label-page {
      page-break-after: always;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: 10mm;
    }
    .label-page img {
      max-width: 100%;
      height: auto;
    }
  </style>
</head>
<body>
  ${labels.map(l => {
                                                const isPDF = l.startsWith('JVBERi') || l.startsWith('JVB') || (l.substring(0, 10).indexOf('JVBER') !== -1);
                                                if (isPDF) {
                                                    return `<div class="label-page"><div style="font-family:sans-serif; text-align:center; margin-top:50px;"><h2>Commercial Invoice PDF</h2><p>Please download this file to print the commercial invoice.</p></div></div>`;
                                                }
                                                return `<div class="label-page"><img src="data:image/gif;base64,${l}"/></div>`;
                                            }).join('')}
</body>
</html>`;
                                            win.document.write(html);
                                            win.document.close();
                                            win.print();
                                        }} className="action-btn secondary" style={{ width: 'auto', marginTop: 0 }}>Print (A4)</button>
                                        <button onClick={() => {
                                            const labels = viewLabel;
                                            if (!labels || !labels.length) return;
                                            labels.forEach((l, idx) => {
                                                const isPDF = l.startsWith('JVBERi') || l.startsWith('JVB') || (l.substring(0, 10).indexOf('JVBER') !== -1);
                                                const a = document.createElement('a');
                                                a.href = isPDF ? `data:application/pdf;base64,${l}` : `data:image/gif;base64,${l}`;
                                                const indexSuffix = labels.length > 1 ? `-${idx + 1}` : '';
                                                a.download = isPDF ? `invoice${indexSuffix}.pdf` : `label${indexSuffix}.gif`;
                                                document.body.appendChild(a);
                                                a.click();
                                                document.body.removeChild(a);
                                            });
                                        }} className="action-btn secondary" style={{ width: 'auto', marginTop: 0 }}>Download</button>
                                        <button onClick={() => setViewLabel(null)} className="action-btn" style={{ width: 'auto', marginTop: 0, background: '#718096', color: 'white' }}>Close</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Billing Modal */}
                        {viewBilling && (
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={() => setViewBilling(null)}>
                                <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', maxWidth: '700px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                                    <h3 style={{ borderBottom: '2px solid #edf2f7', paddingBottom: '1rem', marginBottom: '1rem' }}>Billing Breakdown</h3>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem', background: '#f7fafc', padding: '1rem', borderRadius: '8px' }}>
                                        <div><strong>Tracking:</strong> <span style={{ fontFamily: 'monospace' }}>{viewBilling.trackingNumber}</span></div>
                                        <div><strong>Date:</strong> {new Date(viewBilling.shipmentDate).toLocaleDateString()}</div>
                                        <div><strong>Service:</strong> {viewBilling.service}</div>
                                        <div><strong>Weight:</strong> {viewBilling.billingWeight} KGS</div>
                                    </div>

                                    {viewBilling.detailedBreakdown ? (
                                        <>
                                            <div style={{ marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', color: '#4a5568', marginBottom: '0.5rem' }}>
                                                    <span>📦 Billing Weight</span>
                                                    <span>{viewBilling.detailedBreakdown.totalWeight} KGS</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#2b6cb0', fontSize: '1.2rem', fontWeight: 'bold' }}>
                                                    <span>Total Charges (PKR)</span>
                                                    <span>{viewBilling.detailedBreakdown.grandTotalPKR}</span>
                                                </div>
                                            </div>

                                            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '1px solid #cbd5e0', color: '#718096' }}>
                                                        <th style={{ textAlign: 'left', padding: '6px 0' }}>Item</th>
                                                        <th style={{ textAlign: 'right', padding: '6px 0' }}>USD</th>
                                                        <th style={{ textAlign: 'right', padding: '6px 0' }}>PKR</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ padding: '6px 0' }}>📊 Freight (From Tariff)</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.freightUSD}</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.freightPKR}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '6px 0' }}>SBC (Fixed)</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.sbcUSD}</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.sbcPKR}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '6px 0' }}>IPF - International Processing Fee (Code 573)</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ipfUSD}</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ipfPKR}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '6px 0' }}>Insurance (6.6 USD if &lt; 6600, else 1% of value)</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.insuranceUSD}</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.insurancePKR}</td>
                                                    </tr>

                                                    {parseFloat(viewBilling.detailedBreakdown.deliverySurchargeUSD) > 0 && (
                                                        <tr>
                                                            <td style={{ padding: '6px 0' }}>Delivery Surcharge (Code 190 from UPS)</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.deliverySurchargeUSD}</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.deliverySurchargePKR}</td>
                                                        </tr>
                                                    )}

                                                    {parseFloat(viewBilling.detailedBreakdown.largePkgSurchargeUSD) > 0 && (
                                                        <tr>
                                                            <td style={{ padding: '6px 0' }}>Large Package Surcharge (Code 377 from UPS)</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.largePkgSurchargeUSD}</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.largePkgSurchargePKR}</td>
                                                        </tr>
                                                    )}

                                                    {parseFloat(viewBilling.detailedBreakdown.additionalHandlingUSD) > 0 && (
                                                        <tr>
                                                            <td style={{ padding: '6px 0' }}>Additional Handling (Code 100 from UPS)</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.additionalHandlingUSD}</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.additionalHandlingPKR}</td>
                                                        </tr>
                                                    )}

                                                    {parseFloat(viewBilling.detailedBreakdown.peakSeasonLargePackageUSD || 0) > 0 && (
                                                        <tr>
                                                            <td style={{ padding: '6px 0' }}>Peak Season Surcharge - LP (UPS Code 431)</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.peakSeasonLargePackageUSD}</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.peakSeasonLargePackagePKR}</td>
                                                        </tr>
                                                    )}

                                                    {parseFloat(viewBilling.detailedBreakdown.peakSeasonAdditionalHandlingUSD || 0) > 0 && (
                                                        <tr>
                                                            <td style={{ padding: '6px 0' }}>Peak Season Surcharge - AH (UPS Code 432)</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.peakSeasonAdditionalHandlingUSD}</td>
                                                            <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.peakSeasonAdditionalHandlingPKR}</td>
                                                        </tr>
                                                    )}

                                                    <tr>
                                                        <td style={{ padding: '6px 0' }}>Fuel Surcharge ({viewBilling.detailedBreakdown.fuelUsed})</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.fuelSurchargeUSD}</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.fuelSurchargePKR}</td>
                                                    </tr>

                                                    {(() => {
                                                        const peakUSD = viewBilling.detailedBreakdown?.peakChargesUSD || '0.00';
                                                        const peakPKR = viewBilling.detailedBreakdown?.peakChargesPKR || '0.00';
                                                        const peakAmount = parseFloat(peakUSD);
                                                        console.log('[Shipping Table] Peak Charges Check:', { peakUSD, peakPKR, peakAmount, condition: peakAmount > 0 });
                                                        if (peakAmount > 0) {
                                                            return (
                                                                <tr style={{ backgroundColor: '#fffaf0', fontWeight: 'bold' }}>
                                                                    <td style={{ padding: '6px 0' }}>🔥 Peak Period Charges</td>
                                                                    <td style={{ textAlign: 'right' }}>{peakUSD}</td>
                                                                    <td style={{ textAlign: 'right' }}>{peakPKR}</td>
                                                                </tr>
                                                            );
                                                        }
                                                        return null;
                                                    })()}

                                                    {(() => {
                                                        const specialUSD = viewBilling.detailedBreakdown?.specialSurchargeUSD || '0.00';
                                                        const specialPKR = viewBilling.detailedBreakdown?.specialSurchargePKR || '0.00';
                                                        const specialAmount = parseFloat(specialUSD);
                                                        const specialCountry = viewBilling.detailedBreakdown?.specialSurchargeCountry;
                                                        if (specialAmount > 0 && specialCountry) {
                                                            return (
                                                                <tr style={{ backgroundColor: '#eef2ff', fontWeight: 'bold' }}>
                                                                    <td style={{ padding: '6px 0' }}>🌍 Country-Specific Surcharge ({specialCountry})</td>
                                                                    <td style={{ textAlign: 'right' }}>{specialUSD}</td>
                                                                    <td style={{ textAlign: 'right' }}>{specialPKR}</td>
                                                                </tr>
                                                            );
                                                        }
                                                        return null;
                                                    })()}

                                                    <tr style={{ borderTop: '1px solid #e2e8f0', fontWeight: 'bold' }}>
                                                        <td style={{ padding: '8px 0' }}>TOTAL FREIGHT CHARGES (BEFORE TAX)</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.subtotalUSD}</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.subtotalPKR}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '6px 0' }}>Sales Tax (GST)</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.salesTaxUSD}</td>
                                                        <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.salesTaxPKR}</td>
                                                    </tr>
                                                    <tr style={{ borderTop: '2px solid #2d3748', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                                        <td style={{ padding: '10px 0' }}>TOTAL FREIGHT CHARGES (WITH TAX)</td>
                                                        <td colSpan="2" style={{ textAlign: 'right' }}>PKR {viewBilling.detailedBreakdown.totalPKR}</td>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            {viewBilling.detailedBreakdown.ddp && (
                                                <>
                                                    <div style={{ marginTop: '1rem', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem', color: '#2c5282', borderBottom: '1px solid #bee3f8', paddingBottom: '0.25rem' }}>
                                                        DDP / Landed Cost Breakdown
                                                    </div>
                                                    <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                                                        <tbody>
                                                            <tr>
                                                                <td style={{ padding: '4px 0' }}>Import Duties</td>
                                                                <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ddp.totalDuties}</td>
                                                                <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ddp.totalDutiesPKR}</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0' }}>VAT / Taxes</td>
                                                                <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ddp.totalVAT}</td>
                                                                <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ddp.totalVATPKR}</td>
                                                            </tr>
                                                            {parseFloat(viewBilling.detailedBreakdown.ddp.brokerageFees) > 0 && (
                                                                <tr>
                                                                    <td style={{ padding: '4px 0' }}>Brokerage Fees</td>
                                                                    <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ddp.brokerageFees}</td>
                                                                    <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ddp.brokerageFeesPKR}</td>
                                                                </tr>
                                                            )}
                                                            <tr style={{ color: '#e53e3e' }}>
                                                                <td style={{ padding: '4px 0' }}>F/D Charges</td>
                                                                <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ddp.fdCharges}</td>
                                                                <td style={{ textAlign: 'right' }}>{viewBilling.detailedBreakdown.ddp.fdChargesPKR}</td>
                                                            </tr>
                                                            <tr style={{ borderTop: '1px solid #e2e8f0', fontWeight: 'bold', backgroundColor: '#ebf8ff' }}>
                                                                <td style={{ padding: '6px' }}>TOTAL DDP COST</td>
                                                                <td style={{ textAlign: 'right', padding: '6px' }}>{viewBilling.detailedBreakdown.ddp.totalDDPCost}</td>
                                                                <td style={{ textAlign: 'right', padding: '6px' }}>{viewBilling.detailedBreakdown.ddp.totalDDPCostPKR}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>

                                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#2b6cb0', fontSize: '1.2rem', fontWeight: 'bold', borderTop: '2px solid #2b6cb0', paddingTop: '0.5rem' }}>
                                                        <span>Grand Total (Freight + DDP)</span>
                                                        <span>{viewBilling.detailedBreakdown.grandTotalWithDDPPKR}</span>
                                                    </div>
                                                </>
                                            )}


                                            <div style={{ fontSize: '0.75rem', color: '#718096', fontStyle: 'italic', marginBottom: '1rem' }}>
                                                * Base Currency: USD. Conversion Rate: {viewBilling.detailedBreakdown.rateUsed} PKR. Fuel Rate: {viewBilling.detailedBreakdown.fuelUsed}
                                            </div>
                                        </>
                                    ) : (
                                        <div style={{ marginBottom: '1.5rem' }}>
                                            <h4 style={{ marginBottom: '0.5rem', color: '#4a5568' }}>Charges</h4>
                                            {viewBilling.breakdown && viewBilling.breakdown.map((charge, idx) => (
                                                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px dashed #e2e8f0', fontSize: '0.95rem' }}>
                                                    <span>{charge.Code} - {charge.Description || 'Charge'}</span>
                                                    <span style={{ fontWeight: 'bold' }}>{charge.CurrencyCode} {charge.MonetaryValue}</span>
                                                </div>
                                            ))}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderTop: '2px solid #2d3748', marginTop: '1rem', fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                <span>TOTAL</span>
                                                <span>{viewBilling.currency} {viewBilling.totalCharges}</span>
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ textAlign: 'right' }}>
                                        <button onClick={() => setViewBilling(null)} className="action-btn" style={{ width: 'auto', marginTop: 0, background: '#718096', color: 'white' }}>Close</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Shipment Detail Modal - REDESIGNED */}
                        {showDetails && (
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '2rem' }} onClick={() => setShowDetails(null)}>
                                <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '1100px', maxHeight: '95vh', overflow: 'hidden', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

                                    {/* Modal Header */}
                                    <div style={{ background: 'linear-gradient(135deg, #1a365d 0%, #2b6cb0 100%)', padding: '1.5rem 2rem', color: 'white' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', letterSpacing: '-0.025em' }}>Shipment Manifesto</h2>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '0.5rem' }}>
                                                    <span style={{ fontFamily: 'monospace', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '4px', fontSize: '1rem' }}>{showDetails.trackingNumber}</span>
                                                    <div style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '999px',
                                                        background: showDetails.status === 'voided' ? '#feb2b2' : '#9ae6b4',
                                                        color: showDetails.status === 'voided' ? '#742a2a' : '#22543d',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.75rem',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {showDetails.status ? showDetails.status.toUpperCase() : 'ACTIVE'}
                                                    </div>
                                                </div>
                                            </div>
                                            <button onClick={() => setShowDetails(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: 'white', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
                                                <span style={{ fontSize: '1.5rem' }}>×</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div style={{ padding: '2rem', overflowY: 'auto', flexGrow: 1 }}>

                                        {/* Top Row: Key Info Grid */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', marginBottom: '2.5rem', background: '#f8fafc', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Service Type</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.service || 'UPS Saver (65)'}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Billing Terms</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.isDDP ? 'DDP (Duty/Tax Paid)' : 'P/P (Prepaid)'}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Billable Weight</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.billingWeight || showDetails.weight} KGS</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Total Weight</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{(showDetails.packages || []).reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0).toFixed(2)} KGS</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Shipment Type</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.productType === 'LETTERS' ? 'Letter' : showDetails.productType === 'DOCUMENTS' ? 'Document' : (showDetails.isDocumentNoCommercial ? 'Document' : 'Package')}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Invoice #</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.invoiceNo || 'N/A'}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Booked By</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.user || showDetails.createdBy || 'System'}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Origin Port</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.userCity || 'Pakistan'}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Date/Time</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{new Date(showDetails.shipmentDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Ref 1</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.reference1 || 'N/A'}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Ref 2</label>
                                                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{showDetails.reference2 || 'N/A'}</div>
                                            </div>
                                        </div>

                                        {/* Middle Row: Address Cards */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '2.5rem' }}>
                                            {/* Shipper Card */}
                                            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.25rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', color: '#2b6cb0' }}>
                                                    <span style={{ fontSize: '1.2rem' }}>💳</span>
                                                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.05em' }}>Billing Shipper</h4>
                                                </div>
                                                <div style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#334155' }}>
                                                    {/* Use shipper if available with all details, otherwise fallback to origin */}
                                                    {showDetails.shipper && showDetails.shipper.name ? (
                                                        <>
                                                            <strong style={{ display: 'block', marginBottom: '4px', fontSize: '1rem', color: '#0f172a' }}>{showDetails.shipper.name}</strong>
                                                            {showDetails.shipper.attention && <div>Attn: {showDetails.shipper.attention}</div>}
                                                            {showDetails.shipper.address1 && <div>{showDetails.shipper.address1}</div>}
                                                            {showDetails.shipper.address2 && <div>{showDetails.shipper.address2}</div>}
                                                            <div>{showDetails.shipper.city || ''}{showDetails.shipper.state ? ', ' + showDetails.shipper.state : ''} {showDetails.shipper.postalCode || ''}</div>
                                                            <div>{showDetails.shipper.country || ''}</div>
                                                            {showDetails.shipper.phone && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>📞 {showDetails.shipper.phone}</div>}
                                                            {showDetails.shipper.email && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>✉️ {showDetails.shipper.email}</div>}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <strong style={{ display: 'block', marginBottom: '4px', fontSize: '1rem', color: '#0f172a' }}>{showDetails.origin?.name}</strong>
                                                            {showDetails.origin?.attention && <div>Attn: {showDetails.origin.attention}</div>}
                                                            <div>{showDetails.origin?.address1}</div>
                                                            <div>{showDetails.origin?.address2}</div>
                                                            <div>{showDetails.origin?.city}, {showDetails.origin?.state} {showDetails.origin?.postalCode}</div>
                                                            <div>{showDetails.origin?.country}</div>
                                                            {showDetails.origin?.phone && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>📞 {showDetails.origin.phone}</div>}
                                                        </>
                                                    )}
                                                    {(showDetails.shipper?.account || showDetails.origin?.account) && <div style={{ marginTop: '8px', fontWeight: '600', color: '#2563eb' }}>A/C: {showDetails.shipper?.account || showDetails.origin?.account}</div>}
                                                </div>
                                            </div>

                                            {/* Ship From Card */}
                                            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.25rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', color: '#e67e22' }}>
                                                    <span style={{ fontSize: '1.2rem' }}>📦</span>
                                                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.05em' }}>Ship From (Origin)</h4>
                                                </div>
                                                <div style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#334155' }}>
                                                    <strong style={{ display: 'block', marginBottom: '4px', fontSize: '1rem', color: '#0f172a' }}>{showDetails.origin?.name}</strong>
                                                    {showDetails.origin?.attention && <div>Attn: {showDetails.origin.attention}</div>}
                                                    <div>{showDetails.origin?.address1}</div>
                                                    <div>{showDetails.origin?.address2}</div>
                                                    <div>{showDetails.origin?.city}, {showDetails.origin?.state} {showDetails.origin?.postalCode}</div>
                                                    <div>{showDetails.origin?.country}</div>
                                                    {showDetails.origin?.phone && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>📞 {showDetails.origin.phone}</div>}
                                                </div>
                                            </div>

                                            {/* Ship To Card */}
                                            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.25rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', color: '#059669' }}>
                                                    <span style={{ fontSize: '1.2rem' }}>📍</span>
                                                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.05em' }}>Ship To (Consignee)</h4>
                                                </div>
                                                <div style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#334155' }}>
                                                    <strong style={{ display: 'block', marginBottom: '4px', fontSize: '1rem', color: '#0f172a' }}>{showDetails.destination?.name || showDetails.destination?.company}</strong>
                                                    {showDetails.destination?.attention && <div>Attn: {showDetails.destination.attention}</div>}
                                                    <div>{showDetails.destination?.address1}</div>
                                                    <div>{showDetails.destination?.address2}</div>
                                                    <div>{showDetails.destination?.city}, {showDetails.destination?.state} {showDetails.destination?.postalCode}</div>
                                                    <div>{showDetails.destination?.country}</div>
                                                    {showDetails.destination?.phone && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>📞 {showDetails.destination.phone}</div>}
                                                    {showDetails.destination?.email && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>✉️ {showDetails.destination.email}</div>}
                                                    {showDetails.destination?.taxId && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>🆔 {showDetails.destination.taxIdType || 'TAX ID'}: {showDetails.destination.taxId}</div>}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bottom Grid: Packages, Products & Financials */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                                                {/* Packages Section */}
                                                <div>
                                                    <h4 style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', marginBottom: '0.75rem', borderBottom: '2px solid #f1f5f9', paddingBottom: '0.5rem' }}>Package Dimensions</h4>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                        <thead>
                                                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b' }}>Index</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b' }}>Weight (kg)</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b' }}>L x W x H (cm)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {showDetails.packages?.map((pkg, idx) => (
                                                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                    <td style={{ padding: '10px 12px', fontSize: '0.9rem' }}>{idx + 1}</td>
                                                                    <td style={{ padding: '10px 12px', fontSize: '0.9rem', fontWeight: '600' }}>{pkg.weight}</td>
                                                                    <td style={{ padding: '10px 12px', fontSize: '0.9rem' }}>{pkg.length} x {pkg.width} x {pkg.height}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Products Section */}
                                                {showDetails.products && showDetails.products.length > 0 && (
                                                    <div>
                                                        <h4 style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', marginBottom: '0.75rem', borderBottom: '2px solid #f1f5f9', paddingBottom: '0.5rem' }}>Commodity Declaration</h4>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <thead>
                                                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b' }}>Description</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b' }}>HS Code</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '0.75rem', color: '#64748b' }}>Value ($)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {showDetails.products.map((prod, idx) => (
                                                                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                        <td style={{ padding: '10px 12px', fontSize: '0.9rem' }}>{prod.description}</td>
                                                                        <td style={{ padding: '10px 12px', fontSize: '0.9rem', color: '#64748b', fontFamily: 'monospace' }}>{prod.hsCode || '---'}</td>
                                                                        <td style={{ padding: '10px 12px', fontSize: '0.9rem', textAlign: 'right', fontWeight: '600' }}>{prod.value}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Billing Summary Section */}
                                            <div style={{ background: '#f1f5f9', borderRadius: '12px', padding: '1.5rem', alignSelf: 'start' }}>
                                                <h4 style={{ fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', marginBottom: '1.25rem', textAlign: 'center' }}>Charges Breakdown</h4>

                                                {showDetails.detailedBreakdown ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                            <span style={{ color: '#64748b' }}>Base Freight</span>
                                                            <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.freightPKR}</span>
                                                        </div>

                                                        {parseFloat(showDetails.detailedBreakdown.insuranceUSD || 0) > 0 && (
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                                <span style={{ color: '#64748b' }}>Insurance Fee</span>
                                                                <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.insurancePKR}</span>
                                                            </div>
                                                        )}

                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                            <span style={{ color: '#64748b' }}>SBC (Fixed)</span>
                                                            <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.sbcPKR}</span>
                                                        </div>

                                                        {parseFloat(showDetails.detailedBreakdown.ipfUSD || 0) > 0 && (
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                                <span style={{ color: '#64748b' }}>IPF (Code 573)</span>
                                                                <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.ipfPKR}</span>
                                                            </div>
                                                        )}

                                                        {parseFloat(showDetails.detailedBreakdown.deliverySurchargeUSD || 0) > 0 && (
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                                <span style={{ color: '#64748b' }}>Remote Area Surcharge</span>
                                                                <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.deliverySurchargePKR}</span>
                                                            </div>
                                                        )}

                                                        {parseFloat(showDetails.detailedBreakdown.largePkgSurchargeUSD || 0) > 0 && (
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                                <span style={{ color: '#64748b' }}>Large Pkg Surcharge</span>
                                                                <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.largePkgSurchargePKR}</span>
                                                            </div>
                                                        )}

                                                        {parseFloat(showDetails.detailedBreakdown.additionalHandlingUSD || 0) > 0 && (
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                                <span style={{ color: '#64748b' }}>Additional Handling</span>
                                                                <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.additionalHandlingPKR}</span>
                                                            </div>
                                                        )}

                                                        {parseFloat(showDetails.detailedBreakdown.peakSeasonLargePackageUSD || 0) > 0 && (
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                                <span style={{ color: '#64748b' }}>Peak Season Surcharge - LP (UPS Code 431)</span>
                                                                <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.peakSeasonLargePackagePKR}</span>
                                                            </div>
                                                        )}

                                                        {parseFloat(showDetails.detailedBreakdown.peakSeasonAdditionalHandlingUSD || 0) > 0 && (
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                                <span style={{ color: '#64748b' }}>Peak Season Surcharge - AH (UPS Code 432)</span>
                                                                <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.peakSeasonAdditionalHandlingPKR}</span>
                                                            </div>
                                                        )}

                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                            <span style={{ color: '#64748b' }}>Fuel Surcharge</span>
                                                            <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.fuelSurchargePKR}</span>
                                                        </div>

                                                        {(() => {
                                                            const peakPKR = showDetails.detailedBreakdown?.peakChargesPKR || '0.00';
                                                            const peakAmount = parseFloat(showDetails.detailedBreakdown?.peakChargesUSD || '0');
                                                            console.log('[Shipping Summary] Peak Charges Check:', { peakChargesUSD: showDetails.detailedBreakdown?.peakChargesUSD, peakPKR, peakAmount, condition: peakAmount > 0 });
                                                            if (peakAmount > 0) {
                                                                return (
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', padding: '4px', backgroundColor: '#fff5e6', borderRadius: '2px', fontWeight: 'bold' }}>
                                                                        <span style={{ color: '#d97706' }}>🔥 Peak Period Charges</span>
                                                                        <span style={{ color: '#d97706' }}>PKR {peakPKR}</span>
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        })()}

                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', borderTop: '1px solid #e2e8f0', paddingTop: '4px', marginTop: '4px', fontStyle: 'italic' }}>
                                                            <span style={{ color: '#475569', fontWeight: 'bold' }}>Subtotal (Excl. Tax)</span>
                                                            <span style={{ color: '#475569', fontWeight: 'bold' }}>PKR {showDetails.detailedBreakdown.subtotalPKR}</span>
                                                        </div>

                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                            <span style={{ color: '#64748b' }}>Sales Tax (GST)</span>
                                                            <span style={{ color: '#334155' }}>PKR {showDetails.detailedBreakdown.salesTaxPKR}</span>
                                                        </div>

                                                        {showDetails.detailedBreakdown.ddp && (
                                                            <div style={{ background: '#fff7f7', padding: '12px', borderRadius: '8px', border: '1px solid #fecaca', marginTop: '8px' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#b91c1c', fontWeight: '800', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #fecaca' }}>
                                                                    <span>DDP / Landed Cost Total</span>
                                                                    <span>PKR {showDetails.detailedBreakdown.ddp.totalDDPCostPKR}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.78rem', color: '#6b7280' }}>
                                                                    {parseFloat(showDetails.detailedBreakdown.ddp.totalDuties || 0) > 0 && (
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <span>↳ Import Duties</span>
                                                                            <span>PKR {showDetails.detailedBreakdown.ddp.totalDutiesPKR}</span>
                                                                        </div>
                                                                    )}
                                                                    {parseFloat(showDetails.detailedBreakdown.ddp.totalVAT || 0) > 0 && (
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <span>↳ VAT / Taxes</span>
                                                                            <span>PKR {showDetails.detailedBreakdown.ddp.totalVATPKR}</span>
                                                                        </div>
                                                                    )}
                                                                    {parseFloat(showDetails.detailedBreakdown.ddp.brokerageFees || 0) > 0 && (
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <span>↳ Brokerage Fees</span>
                                                                            <span>PKR {showDetails.detailedBreakdown.ddp.brokerageFeesPKR}</span>
                                                                        </div>
                                                                    )}
                                                                    {parseFloat(showDetails.detailedBreakdown.ddp.fdCharges || 0) > 0 && (
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                                                                            <span>↳ F/D Charges</span>
                                                                            <span>PKR {showDetails.detailedBreakdown.ddp.fdChargesPKR}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #2d3748' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <span style={{ fontSize: '1rem', fontWeight: '900', color: '#1e293b' }}>GRAND TOTAL</span>
                                                                <div style={{ textAlign: 'right' }}>
                                                                    <div style={{ fontSize: '1.6rem', fontWeight: '900', color: '#2563eb', letterSpacing: '-0.025em' }}>
                                                                        {showDetails.detailedBreakdown && (showDetails.detailedBreakdown.grandTotalWithDDPPKR || showDetails.detailedBreakdown.grandTotalPKR)
                                                                            ? (showDetails.detailedBreakdown.grandTotalWithDDPPKR || showDetails.detailedBreakdown.grandTotalPKR)
                                                                            : (showDetails.detailedBreakdown && showDetails.detailedBreakdown.totalPKR
                                                                                ? `PKR ${showDetails.detailedBreakdown.totalPKR}`
                                                                                : `${showDetails.currency || 'USD'} ${showDetails.totalCharges}`)}
                                                                    </div>
                                                                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'bold' }}>Conversion: {showDetails.detailedBreakdown.rateUsed || '280'} PKR</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                                                        <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#2563eb' }}>{showDetails.currency} {showDetails.totalCharges}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Modal Footer Actions */}
                                    <div style={{ padding: '1.5rem 2rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                        <button
                                            onClick={() => handleDuplicateShipment(showDetails)}
                                            className="action-btn"
                                            style={{ width: 'auto', marginTop: 0, background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', fontWeight: 'bold', padding: '0.6rem 1.5rem' }}
                                        >
                                            Duplicate Shipment
                                        </button>
                                        <button
                                            onClick={() => setShowDetails(null)}
                                            className="action-btn"
                                            style={{ width: 'auto', marginTop: 0, background: '#334155', color: '#fff', fontWeight: 'bold', padding: '0.6rem 1.5rem' }}
                                        >
                                            Close Details
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )
            }

            {
                showPaymentModal && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
                        <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '100%', maxWidth: '450px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                            <h3 style={{ margin: '0 0 1.5rem 0', color: '#1a365d', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span>💰</span> Receive Payment
                            </h3>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', color: '#4a5568', fontWeight: '600', marginBottom: '6px' }}>Payment Method</label>
                                <select
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e0', fontSize: '1rem' }}
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                    <option value="Cheque">Cheque</option>
                                </select>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', color: '#4a5568', fontWeight: '600', marginBottom: '6px' }}>Collect Amount (PKR)</label>
                                <input
                                    type="text"
                                    value={collectAmount}
                                    onChange={(e) => setCollectAmount(e.target.value)}
                                    placeholder="Enter amount..."
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e0', fontSize: '1rem' }}
                                />
                            </div>

                            <div style={{ marginBottom: '2rem' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', color: '#4a5568', fontWeight: '600', marginBottom: '6px' }}>Transaction ID / Ref (Optional)</label>
                                <input
                                    type="text"
                                    value={transactionId}
                                    onChange={(e) => setTransactionId(e.target.value)}
                                    placeholder="e.g. Bank Ref # or Cheque #"
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e0', fontSize: '1rem' }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={() => setShowPaymentModal(false)}
                                    className="action-btn"
                                    style={{ flex: 1, margin: 0, background: '#f7fafc', color: '#4a5568', border: '1px solid #cbd5e0' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (!collectAmount) {
                                            alert('Please enter the collected amount.');
                                            return;
                                        }
                                        setIsPaymentReceived(true);
                                        setShowPaymentModal(false);
                                    }}
                                    className="action-btn primary"
                                    style={{ flex: 1, margin: 0 }}
                                >
                                    Save Payment
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {showThirdPartyModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 11000 }}>
                    <div style={{ background: '#f5f7fa', borderRadius: '4px', width: '100%', maxWidth: '420px', boxShadow: '0 12px 30px rgba(0,0,0,0.3)', border: '1px solid #94a3b8', overflow: 'hidden' }}>
                        {/* Title Bar */}
                        <div style={{ background: 'linear-gradient(to bottom, #fff, #e2e8f0)', padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #cbd5e0' }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#334155' }}>Third Party Address</span>
                            <div style={{ display: 'flex', gap: '3px' }}>
                                <button style={{ width: '22px', height: '22px', border: '1px solid #94a3b8', background: '#fff', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
                                <button style={{ width: '22px', height: '22px', border: '1px solid #94a3b8', background: '#fff', fontSize: '1rem', fontWeight: 'bold', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowThirdPartyModal(false)}>×</button>
                            </div>
                        </div>

                        {/* Form Body */}
                        <div style={{ padding: '16px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                        <label style={{ fontSize: '0.72rem', color: '#1a365d', fontWeight: 'bold' }}>Customer ID:</label>
                                        <label style={{ fontSize: '0.68rem', display: 'flex', alignItems: 'center', gap: '4px', color: '#1a365d' }}>
                                            <input type="checkbox" /> Update Address Book
                                        </label>
                                    </div>
                                    <input className="compact-input" value={ddpThirdPartyInfo.customerId} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, customerId: e.target.value })} />
                                </div>

                                <div style={{ gridColumn: 'span 2', marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Company or Name:</label>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <input className="compact-input" value={ddpThirdPartyInfo.name} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, name: e.target.value })} style={{ flex: 1 }} />
                                        <button className="compact-btn" style={{ background: '#fff', border: '1px solid #cbd5e0', padding: '0 8px' }}>🔍</button>
                                    </div>
                                </div>

                                <div style={{ gridColumn: 'span 2', marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Attention:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.attention} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, attention: e.target.value })} />
                                </div>

                                <div style={{ gridColumn: 'span 2', marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Address 1:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.address1} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, address1: e.target.value })} />
                                </div>

                                <div style={{ marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Address 2:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.address2} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, address2: e.target.value })} />
                                </div>
                                <div style={{ marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Address 3:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.address3} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, address3: e.target.value })} />
                                </div>

                                <div style={{ marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Country/Territory:</label>
                                    <select className="compact-input" value={ddpThirdPartyInfo.country} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, country: e.target.value })} style={{ height: '24px' }}>
                                        {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div style={{ marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Postal Code:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.postalCode} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, postalCode: e.target.value })} />
                                </div>

                                <div style={{ marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>City or Town:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.city} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, city: e.target.value })} />
                                </div>
                                <div style={{ marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>State/Province:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.state} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, state: e.target.value })} />
                                </div>

                                <div style={{ marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Telephone:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.phone} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, phone: e.target.value })} />
                                </div>
                                <div style={{ marginTop: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#2b6cb0', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>Fax:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.fax} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, fax: e.target.value })} />
                                </div>

                                <div style={{ marginTop: '6px' }}>
                                    <label style={{ fontSize: '0.72rem', color: '#1a365d', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>UPS Account:</label>
                                    <input className="compact-input" value={ddpThirdPartyInfo.account} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, account: e.target.value.toUpperCase() })} style={{ fontWeight: 'bold', background: '#fff' }} />
                                </div>
                                <div style={{ marginTop: '18px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" checked={ddpThirdPartyInfo.alsoUseForDutyTax} onChange={e => setDdpThirdPartyInfo({ ...ddpThirdPartyInfo, alsoUseForDutyTax: e.target.checked })} id="also-duty" />
                                    <label htmlFor="also-duty" style={{ fontSize: '0.68rem', color: '#334155', fontWeight: 'bold' }}>Also use for Duty and Tax</label>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                                <button className="compact-btn primary" style={{ minWidth: '70px', height: '28px', background: '#fff', border: '1px solid #cbd5e0', fontWeight: 'bold' }} onClick={() => setShowThirdPartyModal(false)}>OK</button>
                                <button className="compact-btn" style={{ minWidth: '70px', height: '28px', background: '#fff', border: '1px solid #cbd5e0' }} onClick={() => { setShowThirdPartyModal(false); setDdpTransportationPayer('BillShipper'); }}>Cancel</button>
                                <button className="compact-btn" style={{ minWidth: '70px', height: '28px', background: '#fff', border: '1px solid #cbd5e0' }}>Help</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Shipping;
